<!-- Copyright (c) 2023 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="customer-main">
    <Menus :menu="menus" @menuClicked="menuClicked" />
    <div class="right-container">
      <TableBox
        title="Customer Profile"
        :tableData="customerList"
        :selectedRow.sync="selectedCustomer"
        :loading="customerLoading"
      />
    </div>

    <PopupEntry
      :show.sync="popupEntryShow"
      v-if="popupEntryShow"
      :title="popupEntryTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="entryRequired"
      :successMessages="entrySuccess"
      :messages="entryMessages"
      :errorMessages="entryError"
      @requiredClicked="entryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="customer-code">
        <Input
          placeholder="Customer Code"
          :inputValue.sync="customerCode"
          :errorMessage.sync="customerCodeError"
          :isRequired="true"
          ref="customerCode"
          :isDisabled="viewMode || popupEntryTitle[0] == 'E'"
          @focusChanged="focusCustomerCode"
          validator="masterfilecode,10,uppercase"
        />
        <!--<CheckBox title="Active" :isChecked.sync="isActive" :isDisabled="viewMode" />-->
      </div>

      <Tab
        :tabs="tabsItems"
        style="height: calc(100% - 29px)"
        :tabIndex.sync="currentTab"
        :preventTabSwitch="true"
        @tabClicked="tabClicked"
      >
        <div slot="General Details" style="display: flex; height: 100%">
          <!-- General Details -->
          <div :style="{ width: showStatusHistory ? '65%' : '100%' }">
            <RadioButtonSet
              style="display: flex; margin-bottom: 10px"
              title="Customer Type"
              :buttons="custTypeButton"
              labelKey="label"
              :selected.sync="selectedCustType"
              :isRequired="true"
              :isDisabled="viewMode || DefaultGLAccData['master_hidecustboth'] == '1'"
              :isHorizontal="true"
              @radioChanged="custTypeChange"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Customer Category"
              :isRequired="true"
              ref="cu_category"
              :items="CustCategoryData"
              itemKey="Description"
              descKey="Customer Category"
              :selectedObject.sync="selectedCustCategory"
              :errorMessage.sync="categoryError"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Name"
              :isRequired="true"
              ref="cu_name"
              :inputValue.sync="cu_name"
              :errorMessage.sync="nameError"
              validator="text,100,uppercase"
            />
            <div class="rowBox">
              <Input
                style="width: 50%"
                :isDisabled="viewMode"
                placeholder="Short Name"
                :isRequired="true"
                ref="cu_sname"
                :inputValue.sync="cu_sname"
                :errorMessage.sync="snameError"
                validator="text,20,uppercase"
              />
              <InputQuery
                :isDisabled="viewMode"
                style="margin: 0 10px 0 10px; width: 16.66%"
                placeholder="Customer Status"
                :isRequired="true"
                ref="cu_status"
                :items="StatusData"
                itemKey="Description"
                descKey="Customer Status"
                :selectedObject.sync="selectedStatus"
                :errorMessage.sync="statusError"
                :showDesc="false"
              />
              <DateTimePicker
                style="width: 16.66%"
                :isDisabled="viewMode"
                placeholder="From"
                :value.sync="StatusValidFr"
                :showTime="false"
                :isRequired="true"
                :errorMessage.sync="StatusValidFrError"
                ref="cu_statusfr"
              />
              <button v-if="popupEntryTitle != 'New Customer'" @click="toggleStatusHistory" style="width: 16.66%">
                {{ toggleStatusHistoryButtonText }}
              </button>
            </div>
            <div class="rowBox">
              <Input
                :isDisabled="viewMode"
                placeholder="Comp Reg. No"
                style="margin: 0 5px 0 0"
                :inputValue.sync="Cus_RegNo"
                :errorMessage.sync="regNoError"
                ref="cu_regno"
                validator="text,20,uppercase"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Subsidiary of [Parent Co. Name]"
                style="margin: 0 0 0 5px"
                :inputValue.sync="Subsidiary"
                :errorMessage.sync="subsidiaryError"
                ref="cu_subsidiary"
                validator="text,10,uppercase"
              />
            </div>
            <AddressFormV2
              :address.sync="addressData"
              :addressErrors.sync="addressErrors"
              :viewMode="viewMode"
              ref="addressForm"
              :postcodes="postcodes"
              :states="states"
              :countries="countries"
            />
            <div class="rowBox">
              <Input
                :isDisabled="viewMode"
                placeholder="Phone No"
                style="margin: 0 5px 0 0"
                :inputValue.sync="cu_phone"
                :errorMessage.sync="phoneError"
                ref="cu_phone"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Fax No"
                style="margin: 0 0 0 5px"
                :inputValue.sync="cu_fax"
                :errorMessage.sync="faxError"
                ref="cu_fax"
              />
            </div>
            <Input
              :isDisabled="viewMode"
              placeholder="Email"
              :inputValue.sync="cu_email"
              :errorMessage.sync="emailError"
              ref="cu_email"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Customer Branch"
              :items="BrCodeData"
              itemKey="Branch Name"
              descKey="Branch Code"
              :selectedObject.sync="selectedBranch"
            />
          </div>
          <div v-if="showStatusHistory" style="width: 35%; margin-left: 10px">
            <TableBox title="Customer Status History" :tableData="statusHistoryData" />
          </div>
        </div>

        <div slot="Contact Details" style="height: 100%">
          <!-- Contact Details -->
          <Contact
            v-if="!tableLoading"
            :showInputs.sync="contactShowInputs"
            :person.sync="contactPerson"
            :personError.sync="contactPersonError"
            :contacts.sync="contacts"
            :viewMode="viewMode"
            ref="contactTab"
            :postcodes="postcodes"
            :states="states"
            :countries="countries"
          />
        </div>

        <div slot="Billing Info" style="height: 100%; display: flex">
          <!-- Billing Info -->
          <div class="billDetails" :style="{ width: billingScheduleShow ? '60%' : '100%' }">
            <!-- Left Side -->
            <InputQuery
              :isDisabled="viewMode || billingCustCodeDisable"
              placeholder="Billing Customer Code"
              :isRequired="true"
              ref="cu_billcustcode"
              :items="formattedCustCodeData"
              itemKey="combinedKey"
              descKey="Customer Code"
              :selectedObject.sync="selectedCustCode"
              :errorMessage.sync="custCodeError"
              :search.sync="customerCodeSearch"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Payment Type"
              :items="formattedPaymentTypeData"
              itemKey="combinedKey"
              descKey="Payment Type"
              :selectedObject.sync="selectedDefaultPayType"
            />
            <Input
              :isDisabled="viewMode"
              placeholder="Bill Term"
              :inputValue.sync="BillingTerm"
              :regex="BillingTermRegex"
              :errorMessage.sync="billingTermError"
              ref="cu_billingterm"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Bill Type"
              :items="BillingTypeData"
              itemKey="Description"
              descKey="BillType"
              :selectedObject.sync="selectedBillingType"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Bill Format"
              :items="formattedBillFormat"
              itemKey="combinedKey"
              descKey="DescriBilling Fmtption"
              :selectedObject.sync="selectedBillingFmt"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Default Currency"
              :items="formattedDefaultCurrencyData"
              itemKey="combinedKey"
              descKey="Currency Code"
              :selectedObject.sync="selectedDefaultCur"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="GL Acc Code"
              :isRequired="true"
              ref="cu_glacccode"
              :items="formattedGLAccCodeData"
              itemKey="combinedKey"
              descKey="Acc No"
              :selectedObject.sync="selectedCu_GLAcc"
              :errorMessage.sync="glAccCodeError"
            />
            <CheckBox :isDisabled="viewMode" title="Print Price on CDA" :isChecked.sync="CDAPrintPrice" />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Internal"
              :items="InternalCustData"
              itemKey="Description"
              descKey="InternalCustType"
              :selectedObject.sync="selectedInternalCust"
            />
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Depot Storage Bill"
              :items="DepotStorageBillTypeData"
              itemKey="Description"
              descKey="DepotStorageBillType"
              :selectedObject.sync="selectedDepotStorageBillType"
            />
            <div class="rowBox">
              <Input
                :isDisabled="viewMode"
                class="shortInput"
                placeholder="Trailer Detention"
                :inputValue.sync="TrDetFreeDaysOrHrs"
                :regex="TrDetFreeDaysOrHrsRegex"
                :errorMessage.sync="TrDetFreeDaysOrHrsError"
                ref="cu_trailerdetention"
              />
              Free Days
            </div>
            <div class="rowBox">
              <Input
                :isDisabled="viewMode"
                class="shortInput"
                placeholder="Trailer Staging"
                :inputValue.sync="TrkStgFreeDaysOrHrs"
                :regex="TrkStgFreeDaysOrHrsRegex"
                :errorMessage.sync="TrkStgFreeDaysOrHrsError"
                ref="cu_trailerstaging"
              />
              Hours
            </div>
            <div class="rowBox">
              <Input
                class="shortInput"
                style="width: 60%"
                placeholder="Billing Schedule"
                :isDisabled="true"
                :inputValue="billingSchedule"
              />
              <button :disabled="billingScheduleShow" @click="changeBillingScheduleClicked" v-if="!viewMode"
                >Change Billing Schedule</button
              >
            </div>
            <InputQuery
              :isDisabled="viewMode"
              placeholder="Invoice Option"
              :items="abInvoiceOptionData"
              itemKey="Description"
              descKey="InvoiceOption"
              :selectedObject.sync="selectedabInvoiceOption"
            />
          </div>
          <div class="scheduleDetails" v-if="billingScheduleShow">
            <!-- Right Side -->
            <div class="buttonBox" style="justify-content: flex-end">
              <button @click="saveBillingSchedule">Save</button>
              <button @click="cancelBillingSchedule" style="margin-left: 5px">Cancel</button>
            </div>
            <div
              class="scheduleBox"
              :style="{
                'border-color': noneBillingScheduleSelected ? 'gray' : 'lightgray',
                color: noneBillingScheduleSelected ? 'black' : 'lightgray',
              }"
            >
              <RadioButtonV2
                class="scheduleTitle"
                label="None"
                name="billingScheduleRadioButtons"
                @radioChanged="scheduleSelectionRadioChanged"
                ref="billingScheduleNoneRadioButton"
              />
              No billing is done.
            </div>
            <div
              class="scheduleBox"
              :style="{
                'border-color': dailyBillingScheduleSelected ? 'gray' : 'lightgray',
                color: dailyBillingScheduleSelected ? 'black' : 'lightgray',
              }"
            >
              <RadioButtonV2
                class="scheduleTitle"
                label="Daily"
                name="billingScheduleRadioButtons"
                @radioChanged="scheduleSelectionRadioChanged"
                ref="billingScheduleDailyRadioButton"
              />
              Billing will be done Daily.
            </div>
            <div
              class="scheduleBox"
              :style="{
                'border-color': weeklyBillingScheduleSelected ? 'gray' : 'lightgray',
                color: weeklyBillingScheduleSelected ? 'black' : 'lightgray',
              }"
            >
              <RadioButtonV2
                class="scheduleTitle"
                label="Weekly"
                name="billingScheduleRadioButtons"
                @radioChanged="scheduleSelectionRadioChanged"
                ref="billingScheduleWeeklyRadioButton"
              />
              <div class="weeklyBox" :style="{ color: weeklyBillingScheduleSelected ? 'black' : 'lightgray' }">
                <CheckBox
                  title="Mon"
                  :isChecked.sync="weeklyBillingScheduleMondayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Tue"
                  :isChecked.sync="weeklyBillingScheduleTuesdayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Wed"
                  :isChecked.sync="weeklyBillingScheduleWednesdayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Thu"
                  :isChecked.sync="weeklyBillingScheduleThursdayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Fri"
                  :isChecked.sync="weeklyBillingScheduleFridayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Sat"
                  :isChecked.sync="weeklyBillingScheduleSaturdayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
                <CheckBox
                  title="Sun"
                  :isChecked.sync="weeklyBillingScheduleSundayChecked"
                  :isDisabled="!weeklyBillingScheduleSelected"
                  @checkBoxClicked="weeklyBillingScheduleCheckboxClicked"
                />
              </div>
              <Input
                class="shortInput"
                style="width: 50%; margin-top: 12px"
                placeholder="Days Selected"
                :inputValue="weeklyBillingScheduleDaysSelected"
                :isDisabled="true"
                :isRequired="true"
                :errorMessage.sync="weeklyBillingScheduleDaysSelectedError"
              />
            </div>
            <div
              class="scheduleBox"
              :style="{
                'border-color': monthlyBillingScheduleSelected ? 'gray' : 'lightgray',
                color: monthlyBillingScheduleSelected ? 'black' : 'lightgray',
              }"
            >
              <RadioButtonV2
                class="scheduleTitle"
                label="Monthly"
                name="billingScheduleRadioButtons"
                @radioChanged="scheduleSelectionRadioChanged"
                ref="billingScheduleMonthlyRadioButton"
              />
              <div :style="{ color: monthlyBillingScheduleSelected ? 'black' : 'lightgray' }">
                Billing will be done every {{ selectedMonthlyDay1 ? selectedMonthlyDay1['CodeValue'] : 'DAY1' }} and
                {{ selectedMonthlyDay2 ? selectedMonthlyDay2['CodeValue'] : 'DAY2' }} of the month.
              </div>
              <Dropdown
                class="shortInput"
                style="width: 50%"
                placeholder="DAY1"
                :items="daysData"
                :isDisabled="!monthlyBillingScheduleSelected"
                itemKey="CodeValue"
                :selectedObject.sync="selectedMonthlyDay1"
                :isRequired="true"
                :errorMessage.sync="monthlyDay1Error"
              />
              <Dropdown
                class="shortInput"
                style="width: 50%"
                placeholder="DAY2"
                :items="daysData"
                :isDisabled="!monthlyBillingScheduleSelected"
                itemKey="CodeValue"
                :selectedObject.sync="selectedMonthlyDay2"
                :isRequired="true"
                :errorMessage.sync="monthlyDay2Error"
              />
              <div
                class="alertBox"
                :style="{
                  'border-color': monthlyBillingScheduleSelected ? '#ff000088' : 'lightgray',
                  color: monthlyBillingScheduleSelected ? '#ff0000' : 'lightgray',
                }"
              >
                <div class="alertTitle">NOTE</div>
                System will not run auto-billing for months where selected days DO NOT exist.
              </div>
            </div>
            <div
              class="scheduleBox"
              :style="{
                'border-color': quarterlyBillingScheduleSelected ? 'gray' : 'lightgray',
                color: quarterlyBillingScheduleSelected ? 'black' : 'lightgray',
              }"
            >
              <RadioButtonV2
                class="scheduleTitle"
                label="Quarterly"
                name="billingScheduleRadioButtons"
                @radioChanged="scheduleSelectionRadioChanged"
                ref="billingScheduleQuarterlyRadioButton"
              />
              <div :style="{ color: quarterlyBillingScheduleSelected ? 'black' : 'lightgray' }">
                Every month #{{ selectedQuarterMonth ? parseInt(selectedQuarterMonth['CodeValue']) : 'MONTH' }} of each
                quarter, on day #{{ selectedQuarterDay ? selectedQuarterDay['CodeValue'] : 'DAY' }} of the month.
              </div>
              <Dropdown
                class="shortInput"
                placeholder="MONTH"
                :items="quarterData"
                :isDisabled="!quarterlyBillingScheduleSelected"
                itemKey="CodeValue"
                :selectedObject.sync="selectedQuarterMonth"
                :isRequired="true"
                :errorMessage.sync="quarterMonthError"
              />
              <Dropdown
                class="shortInput"
                placeholder="DAY"
                :items="daysData"
                :isDisabled="!quarterlyBillingScheduleSelected"
                itemKey="CodeValue"
                :selectedObject.sync="selectedQuarterDay"
                :isRequired="true"
                :errorMessage.sync="quarterDayError"
              />
              <div
                class="alertBox"
                :style="{
                  'border-color': quarterlyBillingScheduleSelected ? '#ff000088' : 'lightgray',
                  color: quarterlyBillingScheduleSelected ? '#ff0000' : 'lightgray',
                }"
              >
                <div class="alertTitle">NOTE</div>
                System will not run auto-billing for months where selected days DO NOT exist.
              </div>
            </div>
          </div>
        </div>
        <div slot="Credit Management" style="height: 100%">
          <!-- Credit Management -->
          <div class="custCredTop">
            <div class="custCredSection" style="margin: 0 5px 0 0">
              <InputQuery
                :isDisabled="viewMode"
                placeholder="Credit Controller"
                :items="CreditControllerData"
                itemKey="Employee Name"
                descKey="EmployeeID"
                :selectedObject.sync="selectedCreditController"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Credit Limit"
                :inputValue.sync="CreditLimit"
                :regex="creditLimitRegex"
                :errorMessage.sync="creditLimitError"
                ref="cu_creditlimit"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Credit Term"
                :inputValue.sync="CreditTerm"
                :regex="creditTermRegex"
                :errorMessage.sync="creditTermError"
                ref="cu_creditterm"
              />
              <InputQuery
                :isDisabled="viewMode"
                placeholder="Credit Type"
                :items="CreditTypeData"
                itemKey="Description"
                descKey="Credit Type"
                :selectedObject.sync="selectedCreditType"
                :isRequired="creditTypeRequired"
                :errorMessage.sync="creditTypeError"
                ref="cu_credittype"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Credit Rating"
                :inputValue.sync="CrRating"
                :regex="crRatingRegex"
                :errorMessage.sync="crRatingError"
                ref="cu_creditrating"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Overdraft Ammount"
                :inputValue.sync="cu_overpay"
                :regex="cu_overpayRegex"
                :errorMessage.sync="cu_overpayError"
                ref="cu_overdraft"
              />
              <Input
                :isDisabled="viewMode"
                placeholder="Cash Float Ammount"
                :inputValue.sync="cu_crdlmt"
                :regex="cu_crdlmtRegex"
                :errorMessage.sync="cu_crdlmtError"
                ref="cu_cashfloat"
              />
              <Input placeholder="Current Outstanding" :isDisabled="true" :inputValue.sync="CurrentOutStanding" />
            </div>
            <div class="custCredSection" style="margin: 0 0 0 5px">
              <div class="custCredExtend">
                <div class="custCredExtendTitle">Extended Credit</div>
                <InputQuery
                  :isDisabled="viewMode || !extCreditEnabled"
                  placeholder="Approved By"
                  :items="ApprovedByData"
                  itemKey="Employee Name"
                  descKey="EmployeeID"
                  :selectedObject.sync="selectedApprovedBy"
                  :isRequired="extCreditEnabled"
                  :errorMessage.sync="approvedByError"
                />
                <Input
                  :isDisabled="viewMode || !extCreditEnabled"
                  placeholder="Extended Credit Limit"
                  :inputValue.sync="ExtCreditLimit"
                  :regex="creditLimitRegex"
                  :isRequired="extCreditEnabled"
                  :errorMessage.sync="extCreditLimitError"
                />
                <Input
                  :isDisabled="viewMode || !extCreditEnabled"
                  placeholder="Extended Credit Term"
                  :inputValue.sync="ExtCreditTerm"
                  :regex="creditTermRegex"
                  :isRequired="extCreditEnabled"
                  :errorMessage.sync="extCreditTermError"
                />
                <InputQuery
                  :isDisabled="true"
                  placeholder="Extended Credit Type"
                  :items="CreditTypeData"
                  itemKey="Description"
                  descKey="Credit Type"
                  :selectedObject.sync="selectedCreditType"
                  :isRequired="extCreditEnabled"
                />
                <div class="rowBox" style="align-items: flex-end">
                  <div class="custCredExtendDate">
                    <DateTimePicker
                      :isDisabled="viewMode || !extCreditEnabled"
                      placeholder="Valid From"
                      :value.sync="ValidFr"
                      :isRequired="extCreditEnabled"
                      :errorMessage.sync="ValidFrError"
                    />
                    <DateTimePicker
                      :isDisabled="viewMode || !extCreditEnabled"
                      placeholder="Valid To"
                      :value.sync="ValidTo"
                      :isRequired="extCreditEnabled"
                      :errorMessage.sync="ValidToError"
                    />
                  </div>
                  <div class="custCredExtendButtons" style="margin: 0 0 0 20px; display: flex; flex-direction: row">
                    <button v-if="SASShowExtCredit && !extCreditEnabled && !viewMode" @click="enableExtCredFields"
                      >Extended Credit</button
                    >
                    <button v-if="extCreditEnabled && !viewMode" @click="applyExtCredFields" style="margin: 0 5px 0 0"
                      >Apply</button
                    >
                    <button v-if="extCreditEnabled && !viewMode" @click="cancelExtCredFields">Cancel</button>
                    <!-- <button v-if="extCreditEnabled && viewMode == false" @click="applyExtCredFields" style="margin: 0 5px 0 0">
                      <img style="width: 25px;  height: 25px;" src="https://dsmapi.logistics.systems/dsm6gIcons/Save.png"> Apply</button>
                    <button v-if="extCreditEnabled && viewMode == false" @click="cancelExtCredFields">
                      <img style="width: 25px;  height: 25px;" src="https://dsmapi.logistics.systems/dsm6gIcons/Cancel.png"> Cancel</button> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
          <TableBox
            v-if="popupEntryTitle != 'New Customer'"
            style="height: 100%"
            title="Extended Credit History"
            :tableData="AuditExtendedCreditData"
          />
        </div>
        <div slot="Sales & Service Team" style="height: 100%; display: flex">
          <!-- Sales & Service Team -->
          <div class="custSalesDisplay" :style="{ width: showCustSalesDetails ? '60%' : ' 100%' }">
            <div v-if="!viewMode">
              <button @click="addCustSales">Add</button>
              <button @click="deleteCustSales" style="margin-left: 5px">Delete</button>
            </div>
            <TableBox
              v-if="!tableLoading"
              style="height: calc(100% - 28px)"
              :additionalButtons="viewMode ? [] : custSalesEditButton"
              title="Sales & Service Teams"
              :tableData="SalesServiceData"
              :selectedRow.sync="selectedSalesService"
              @buttonClicked="salesServiceTableBoxButtonClicked"
              :hideColumns="['Cu_Number', 'UserID', 'CoCode', 'SalesAction']"
              :maxSelection="-1"
            />
          </div>
          <div v-if="showCustSalesDetails" style="width: 40%; margin: 5px 10px">
            <div style="display: flex; justify-content: flex-end">
              <button @click="saveCustSales">Save</button>
              <button @click="cancelCustSales" style="margin-left: 5px">Cancel</button>
            </div>
            <InputQuery
              placeholder="Customer System"
              :isRequired="true"
              :items="SystemData"
              itemKey="Application Name"
              descKey="System"
              :selectedObject.sync="selectedSystem"
              :errorMessage.sync="systemError"
            />
            <InputQuery
              placeholder="Job Type"
              :isRequired="true"
              :items="JobTypeData"
              itemKey="JobTypeDesc"
              descKey="JobType"
              :selectedObject.sync="selectedJobType"
              :errorMessage.sync="jobTypeError"
            />
            <InputQuery
              placeholder="Sales Team"
              :items="SalesTeamData"
              itemKey="Sales Team Name"
              descKey="Sales Team Code"
              :selectedObject.sync="selectedSalesTeam"
            />
            <Input placeholder="Sales Person" :inputValue.sync="SalesPerson" :errorMessage.sync="salesPersonError" />
            <InputQuery
              placeholder="Service Team"
              :items="ServiceTeamData"
              itemKey="Service Team Name"
              descKey="Service Team Code"
              :selectedObject.sync="selectedServiceTeam"
            />
            <Input
              placeholder="Service Person"
              :inputValue.sync="ServicePerson"
              :errorMessage.sync="servicePersonError"
            />
            <CheckBox title="Default" :isChecked.sync="Default" />
          </div>
        </div>
        <div slot="Attachments" style="display: flex; height: 100%">
          <!-- Attachments -->
          <Attachments style="width: 100%" :attachments.sync="attachmentsData" :viewMode="viewMode" />
        </div>
        <div slot="GST/SST/eInvoice">
          <!-- GST/SST/eInvoice -->
          <GST :viewMode="viewMode" :gstData.sync="gstData" ref="gstTab" />
          <BoxContainer title="SST Details" style="margin-bottom: 20px">
            <Input
              placeholder="License No"
              :inputValue.sync="SSTLicenseNo"
              :isDisabled="viewMode"
              ref="SSTLicenseNo"
              validator="text,50,uppercase"
              :errorMessage.sync="SSTLicenseNoError"
            />
          </BoxContainer>
          <BoxContainer title="LHDN eInvoice Details">
            <div class="lhdn-tin">
              <button @click="validateTaxPayerTin" :disabled="validateTINIcon != ''"> Validate </button>
              <div v-if="validateTINIcon != ''" style="margin-right: -5px; margin-left: 5px">
                <Tooltip :icon="validateTINIcon" :tooltipText="validateTINMsg" />
              </div>
              <Input
                style="margin-left: 5px"
                placeholder="Tax Identification No (TIN)"
                :inputValue.sync="eInvoiceTIN"
                :isDisabled="viewMode"
                ref="eInvoiceTIN"
                validator="text,13,uppercase,alphanumeric,none"
                :errorMessage.sync="eInvoiceTINError"
              />
            </div>
          </BoxContainer>
          <div slot="Tax Exemption" style="height: 100%; display: flex">
            <div class="custTaxDisplay" :style="{ width: showCustTaxExempt ? '60%' : ' 100%', 'padding-top': '10px' }">
              <div v-if="!viewMode" style="display: flex; justify-content: flex-end; margin-bottom: 3px">
                <button @click="addCustTaxExempt">Add</button>
                <button @click="deleteCustTaxExempt" style="margin-left: 3px">Delete</button>
              </div>
              <TableBox
                v-if="!tableLoading"
                style="height: calc(100% - 28px)"
                :additionalButtons="viewMode ? [] : custSalesEditButton"
                title="Tax Exemption Records"
                :tableData="TaxExemptData"
                :selectedRow.sync="selectedTaxRecord"
                @buttonClicked="taxExemptTableBoxButtonClicked"
                :hideColumns="['Cust_Code', 'CoCode', 'taxAction']"
                :maxSelection="-1"
              />
            </div>
            <div v-if="showCustTaxExempt" style="width: 40%; margin: 5px 10px; padding-top: 10px">
              <div style="display: flex; justify-content: flex-end; margin-bottom: 5px">
                <button @click="saveTaxExempt">{{ editTaxExemptIndex > -1 ? 'Edit' : 'Save' }}</button>
                <button @click="cancelCustTaxExempt" style="margin-left: 5px">Cancel</button>
              </div>
              <InputQuery
                placeholder="Charge Code"
                :isRequired="true"
                :items="ChargeCodeData"
                itemKey="Charge Code"
                descKey="Description"
                :selectedObject.sync="selectedChargeCode"
                :errorMessage.sync="chargeCodeError"
              />
              <InputQuery
                placeholder="Tax Code"
                :isRequired="true"
                :items="TaxCodeData"
                itemKey="Tax Code"
                descKey="Tax Code Desc"
                :selectedObject.sync="selectedTETaxCode"
                ref="taxCodeField"
                :errorMessage.sync="TETaxCodeError"
              />
            </div>
          </div>
        </div>
      </Tab>
    </PopupEntry>
  </div>
</template>

<script>
/* eslint-disable */
import Contact from '@/components/tabComponents/ContactDetails.vue';
import Attachments from '@/components/tabComponents/Attachments.vue';
import GST from '@/components/tabComponents/GSTDetails.vue';

export default {
  components: {
    Contact,
    Attachments,
    GST,
  },
  computed: {
    toggleStatusHistoryButtonText: function () {
      return this.showStatusHistory ? 'Hide Status History' : 'Show Status History';
    },
    billingSchedule: function () {
      switch (
        this.billingScheduleCode.substring(0, 1) // first character
      ) {
        case '':
          return 'None';

        case 'D':
          return 'Daily';

        case 'W':
          const dayNumberToDayStringMapping = {
            1: 'Mon',
            2: 'Tue',
            3: 'Wed',
            4: 'Thu',
            5: 'Fri',
            6: 'Sat',
            7: 'Sun',
          };
          let daysSelected = this.billingScheduleCode.substring(1).split('0'); // from 2nd character onwards till the end of the string, split on "0"
          daysSelected.splice(0, 1); // remove the first element, which should always be an empty string
          daysSelected = daysSelected.map((dayNumber) => dayNumberToDayStringMapping[dayNumber]); // convert each day number into their string representations
          return `Weekly - ${daysSelected.join(', ')}`;

        case 'M':
          const day1 = this.billingScheduleCode.substring(1, 3); // 2nd and 3rd characers
          const day2 = this.billingScheduleCode.substring(3); // 4th and 5th chracters
          return `Monthly - Every ${day1} and ${day2} of the month`;

        case 'Q':
          const monthNumber = parseInt(this.billingScheduleCode.substring(1, 3)); // 2nd and 3rd characers, should only ever be "01", "02" or "03"
          const monthList = ['Jan, Apr, Jul, Oct', 'Feb, May, Aug, Nov', 'Mar, Jun, Sep, Dec'];
          const day = this.billingScheduleCode.substring(3); // 4th and 5th chracters
          return `Quarterly - Every month #${monthNumber} of the quarter (${monthList[monthNumber - 1]}) on day ${day}`;
      }

      return ''; // should never reach this point
    },
    noneBillingScheduleSelected: function () {
      return !this.selectedBillingSchedule || this.selectedBillingSchedule === 'None';
    },
    dailyBillingScheduleSelected: function () {
      return this.selectedBillingSchedule === 'Daily';
    },
    weeklyBillingScheduleSelected: function () {
      return this.selectedBillingSchedule === 'Weekly';
    },
    weeklyBillingScheduleDaysSelected: function () {
      let selectedDays = [];

      if (this.weeklyBillingScheduleMondayChecked) {
        selectedDays.push('Mon');
      }
      if (this.weeklyBillingScheduleTuesdayChecked) {
        selectedDays.push('Tue');
      }
      if (this.weeklyBillingScheduleWednesdayChecked) {
        selectedDays.push('Wed');
      }
      if (this.weeklyBillingScheduleThursdayChecked) {
        selectedDays.push('Thu');
      }
      if (this.weeklyBillingScheduleFridayChecked) {
        selectedDays.push('Fri');
      }
      if (this.weeklyBillingScheduleSaturdayChecked) {
        selectedDays.push('Sat');
      }
      if (this.weeklyBillingScheduleSundayChecked) {
        selectedDays.push('Sun');
      }

      return selectedDays.join(', ');
    },
    monthlyBillingScheduleSelected: function () {
      return this.selectedBillingSchedule === 'Monthly';
    },
    quarterlyBillingScheduleSelected: function () {
      return this.selectedBillingSchedule === 'Quarterly';
    },
    creditTypeRequired: function () {
      return Boolean(
        this.extCreditEnabled ||
          this.selectedApprovedBy ||
          this.ExtCreditLimit ||
          this.ExtCreditTerm ||
          this.ValidFr ||
          this.ValidTo,
      );
      // if either the extended credit fields are enabled, or if there is an input in any one of the extended credit fields
      // because credit type needs to stay as required even after extended credit is applied
    },
    formattedCustCodeData() {
      return this.CustCodeData.map((item) => ({
        ...item,
        combinedKey: `${item['Customer Code']} - ${item['Customer Name']}`,
      }));
    },
    formattedPaymentTypeData() {
      return this.DefaultPayTypeData.map((item) => ({
        ...item,
        combinedKey: `${item['Payment Type']} - ${item['Description']}`,
      }));
    },
    formattedBillFormat() {
      return this.BillingFmtData.map((item) => ({
        ...item,
        combinedKey: `${item['Billing Fmt']} - ${item['Description']}`,
      }));
    },
    formattedDefaultCurrencyData() {
      return this.DefaultCurData.map((item) => ({
        ...item,
        combinedKey: `${item['Currency Code']} - ${item['Description']}`,
      }));
    },
    formattedGLAccCodeData() {
      return this.Cu_GLAccData.map((item) => ({
        ...item,
        combinedKey: `${item['Acc No']} - ${item['Description']}`,
      }));
    },
  },
  data: function () {
    return {
      tableLoading: false,

      menus: [
        {
          title: 'New Customer',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit Customer',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete Customer',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        }, //api response logic abit weird ("Hide Delete" instead of "Delete")
        {
          title: 'View Customer',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: false,
        },
      ],

      customerLoading: false,
      customerList: [],
      selectedCustomer: [],

      popupEntryShow: false,
      popupEntryTitle: '',
      createdBy: '',
      updatedBy: '',
      viewMode: false,
      entryRequired: [],
      entrySuccess: [],
      entryMessages: [],
      entryError: [],

      customerCode: '',
      customerCodeError: '',
      customerCodeSearch: '',

      lastUpdate: '',

      tabsItems: [
        { title: 'General Details', disabled: false },
        { title: 'Contact Details', disabled: false },
        { title: 'Billing Info', disabled: false },
        { title: 'Credit Management', disabled: false },
        { title: 'Sales & Service Team', disabled: false },
        { title: 'Attachments', disabled: false },
        { title: 'GST/SST/eInvoice', disabled: false },
      ],
      currentTab: 0,

      errorMapping: [
        { key: 'customerCode', tab: -1 },
        { key: 'custType', tab: 0 },
        { key: 'cu_category', tab: 0 },
        { key: 'cu_name', tab: 0 },
        { key: 'cu_sname', tab: 0 },
        { key: 'cu_status', tab: 0 },
        { key: 'cu_statusfr', tab: 0 },
        { key: 'cu_regno', tab: 0 },
        { key: 'cu_subsidiary', tab: 0 },
        { key: 'cu_addressline1', tab: 0 },
        { key: 'cu_addressline2', tab: 0 },
        { key: 'cu_addressline3', tab: 0 },
        { key: 'cu_addressline4', tab: 0 },
        { key: 'cu_city', tab: 0 },
        { key: 'cu_phone', tab: 0 },
        { key: 'cu_fax', tab: 0 },
        { key: 'cu_email', tab: 0 },
        { key: 'cu_contact', tab: 1 },
        { key: 'cu_billcustcode', tab: 2 },
        { key: 'cu_billingterm', tab: 2 },
        { key: 'cu_trailerdetention', tab: 2 },
        { key: 'cu_trailerstaging', tab: 2 },
        { key: 'cu_glacccode', tab: 2 },
        { key: 'cu_creditlimit', tab: 3 },
        { key: 'cu_creditterm', tab: 3 },
        { key: 'cu_credittype', tab: 3 },
        { key: 'cu_creditrating', tab: 3 },
        { key: 'cu_overdraft', tab: 3 },
        { key: 'cu_cashfloat', tab: 3 },
        { key: 'gst_licenseno', tab: 6 },
        { key: 'gst_rmcdapprovalno', tab: 6 },
        { key: 'SSTLicenseNo', tab: 6 },
        { key: 'eInvoiceTIN', tab: 6 },
      ],

      //#region General Details data
      custTypeRequired: false,

      custTypeButton: [
        { label: 'Both', keyValue: '0' },
        { label: 'Customer (Shipper/Consignee)', keyValue: '1' },
        { label: 'ArCustomer (Bill To)', keyValue: '2' },
      ],
      selectedCustType: null,
      DefaultGLAccData: [],
      billingCustCodeDisable: false,
      CustCategoryData: [],
      selectedCustCategory: null,
      categoryError: '',
      cu_name: '',
      nameError: '',
      cu_sname: '',
      snameError: '',
      StatusData: [],
      selectedStatus: null,
      statusError: '',
      StatusValidFr: null,
      StatusValidFrError: '',
      showStatusHistory: false,
      statusHistoryData: [],
      Cus_RegNo: '',
      regNoError: '',
      Subsidiary: '',
      subsidiaryError: '',
      addressData: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        postcode: '',
        city: '',
        state: '',
        country: '',
      },
      addressErrors: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressLine4: '',
        city: '',
      },
      cu_phone: '',
      phoneError: '',
      cu_fax: '',
      faxError: '',
      cu_email: '',
      emailError: '',
      BrCodeData: [],
      selectedBranch: null,
      //#endregion

      //#region Contact Details data
      contactShowInputs: false,
      contactPerson: '',
      contactPersonError: '',
      contacts: [],
      postcodes: [],
      states: [],
      countries: [],

      originalContact: [],
      //#endregion

      //#region Billing Info data
      billingScheduleShow: false,
      CustCodeData: [],
      selectedCustCode: null,
      custCodeError: '',
      DefaultPayTypeData: [],
      selectedDefaultPayType: null,
      BillingTerm: '',
      billingTermError: '',
      BillingTermRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },
      BillingTypeData: [],
      selectedBillingType: null,
      BillingFmtData: [],
      selectedBillingFmt: null,
      DefaultCurData: [],
      selectedDefaultCur: null,
      Cu_GLAccData: [],
      selectedCu_GLAcc: null,
      glAccCodeError: '',
      CDAPrintPrice: false,
      InternalCustData: [],
      selectedInternalCust: null,
      DepotStorageBillTypeData: [],
      selectedDepotStorageBillType: null,
      TrDetFreeDaysOrHrs: '',
      TrDetFreeDaysOrHrsError: '',
      TrDetFreeDaysOrHrsRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },
      TrkStgFreeDaysOrHrs: '',
      TrkStgFreeDaysOrHrs: '',
      TrkStgFreeDaysOrHrsRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },
      abInvoiceOptionData: [],
      selectedabInvoiceOption: null,

      selectedBillingSchedule: '', // stores the selected radio button label
      weeklyBillingScheduleMondayChecked: false,
      weeklyBillingScheduleTuesdayChecked: false,
      weeklyBillingScheduleWednesdayChecked: false,
      weeklyBillingScheduleThursdayChecked: false,
      weeklyBillingScheduleFridayChecked: false,
      weeklyBillingScheduleSaturdayChecked: false,
      weeklyBillingScheduleSundayChecked: false,
      weeklyBillingScheduleDaysSelectedError: '',
      daysData: [],
      quarterData: [],
      selectedMonthlyDay1: null,
      selectedMonthlyDay2: null,
      monthlyDay1Error: '',
      monthlyDay2Error: '',
      selectedQuarterMonth: null,
      quarterMonthError: '',
      selectedQuarterDay: null,
      quarterDayError: '',

      billingScheduleCode: '', // only sent to API
      //#endregion

      //#region Credit Management data
      CreditControllerData: [],
      selectedCreditController: null,
      CreditLimit: '',
      creditLimitError: '',
      creditLimitRegex: {
        regex: /\d*\.?\d*/g,
        live: true,
        autoClear: false,
      },
      CreditTerm: '',
      creditTermError: '',
      creditTermRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },
      CreditTypeData: [],
      selectedCreditType: null,
      creditTypeError: '',
      CrRating: '',
      crRatingError: '',
      crRatingRegex: {
        regex: /\d/g,
        live: true,
        autoClear: false,
      },
      cu_overpay: '',
      cu_overpayError: '',
      cu_overpayRegex: {
        regex: /\d*\.?\d*/g,
        live: true,
        autoClear: false,
      },
      cu_crdlmt: '',
      cu_crdlmtError: '',
      cu_crdlmtRegex: {
        regex: /\d*\.?\d*/g,
        live: true,
        autoClear: false,
      },
      CurrentOutStanding: '',
      ApprovedByData: [],
      selectedApprovedBy: null,
      approvedByError: '',

      SASShowExtCredit: true, // stores SAS value for "Show Ext. Credit"
      extCreditEnabled: false,
      ExtCreditLimit: '',
      extCreditLimitError: '',
      ExtCreditTerm: '',
      extCreditTermError: '',
      ValidFr: null,
      ValidFrError: '',
      ValidTo: null,
      ValidToError: '',
      AuditExtendedCreditData: [],
      //#endregion

      //#region Sales & Service Team data
      showCustSalesDetails: false,
      custSalesEditButton: [{ text: 'Edit', index: 0, width: 60 }],
      SalesServiceData: [],
      selectedSalesService: [],
      SystemData: [],
      selectedSystem: null,
      systemError: '',
      JobTypeData: [],
      selectedJobType: null,
      jobTypeError: '',
      SalesTeamData: [],
      selectedSalesTeam: null,
      SalesPerson: '',
      salesPersonError: '',
      ServiceTeamData: [],
      selectedServiceTeam: null,
      ServicePerson: '',
      servicePersonError: '',
      Default: false,

      editSalesServiceIndex: -1,

      originalSalesService: [],
      //#endregion

      //#region Attachments data
      attachmentsData: [],

      originalAttachments: [],
      //#endregion

      //#region GST/SST/eInvoice data
      gstData: {
        comRegNo: '',
        dateVerified: null,
        licenseNo: '',
        licenseNoError: '',
        rmcdApprovalNo: '',
        rmcdApprovalNoError: '',
        taxCodeItems: [],
        selectedTaxCode: null,
      },
      SSTLicenseNo: '',
      SSTLicenseNoError: '',
      eInvoiceTIN: '',
      eInvoiceTINError: '',
      validateTINIcon: '',
      validateTINMsg: 'Not Validated',

      showCustTaxExempt: false,
      TaxExemptData: [],
      selectedTaxRecord: [],
      ChargeCodeData: [],
      TaxCodeData: [],
      selectedChargeCode: null,
      chargeCodeError: '',
      selectedTETaxCode: '',
      TETaxCodeError: '',

      editTaxExemptIndex: -1,

      originalTaxExempt: [],
      //#endregion
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearTabFields();
      this.clearEntryMessages();

      switch (e['title']) {
        case 'New Customer':
          this.popupEntryShow = true;
          this.popupEntryTitle = 'New Customer';
          this.createdBy = '';
          this.updatedBy = '';
          this.CustCodeData = this.customerList;
          break;
        case 'Edit Customer':
          if (this.selectedCustomer.length == 0) {
            this.$showAlert('error', 'No Customer Selected', 'Please select a customer to edit');
            break;
          }

          await this.populateCustomerDetails(this.selectedCustomer[0]['Customer Code']);
          this.popupEntryTitle = 'Edit Customer';
          this.popupEntryShow = true;
          this.CustCodeData = this.customerList;
          break;
        case 'Delete Customer':
          if (this.selectedCustomer.length == 0) {
            this.$showAlert('error', 'No Customer Selected', 'Please select a customer to delete');
            break;
          }

          const deleting = await this.$showAlert(
            'warning',
            'Delete Customer',
            `Are you sure you want to delete customer ${this.selectedCustomer[0]['Customer Code']}?`,
            [
              { text: 'Delete', textColor: 'white', color: 'red' },
              { text: 'Cancel', textColor: 'black', color: 'lightgray' },
            ],
          );
          if (deleting === 'Cancel') {
            break;
          }

          this.$fullscreenLoading('Deleting Customer');
          let result = await this.$apiCallV2('/CustomerProfile/SaveCustomer', {
            action: 'D',
            cu_number: this.selectedCustomer[0]['Customer Code'],
          });
          this.$fullscreenLoading();

          if (result[0]['Results'] != 0) {
            this.$showAlert(
              'error',
              'Customer NOT Deleted',
              `There was an error deleting your customer: ${result[0]['ErrMsg']}`,
            );
            break;
          }

          this.$showAlert('success', 'Customer Deleted', `Successfully deleted customer ${result[0]['cu_number']}`);
          this.customerList = [];
          this.selectedCustomer = [];
          this.getCustomers();
          break;

        case 'View Customer':
          if (this.selectedCustomer.length == 0) {
            this.$showAlert('error', 'No Customer Selected', 'Please select a customer to view');
            break;
          }

          await this.populateCustomerDetails(this.selectedCustomer[0]['Customer Code']);
          this.popupEntryTitle = 'View Customer';
          this.viewMode = true;
          this.popupEntryShow = true;
          this.CustCodeData = this.customerList;
          break;
      }
    },
    getCustomers: async function () {
      this.customerLoading = true;

      this.$fullscreenLoading('Getting Customers');
      this.customerList = await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'customer' });
      this.$fullscreenLoading();
      this.customerLoading = false;
    },
    populateCustomerDetails: async function (customer) {
      this.$fullscreenLoading('Getting Details');
      const details = (
        await this.$apiCallV2('/Master/GetDetailsMaster', {
          masterfile: 'customer',
          module: 'MASTER',
          recordid: customer,
        })
      )['Customer'];

      this.$fullscreenLoading('Getting Attachments');
      let attachments = await this.$apiCallV2(
        `/CustomerProfile/GetMasterFileAttachments?refno=${customer}&tablename=CUSTOMER`,
      );
      attachments.forEach((a) => {
        a['FileName'] = a['File Name'];
        delete a['File Name'];
      });
      this.customerCode = this.populateTextTrim(details['cu_number']);
      //this.isActive = details['Status'] == 'A';

      this.lastUpdate = details['DateUpdate'];
      this.createdBy = `${details['UserCreated'] || ''} ${details['TimeCreated'] ? new Date(details['TimeCreated']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      this.updatedBy = `${details['UserID'] || ''} ${details['DateUpdate'] ? new Date(details['DateUpdate']).formatString('dd/MM/yyyy HH:mm') : ''}`;

      //#region populate General Details tab
      this.selectedCustType = this.populateObject(this.custTypeButton, 'keyValue', details['CustType']);
      this.selectedCustCategory = this.populateObject(
        this.CustCategoryData,
        'Customer Category',
        details['CustCategory'],
      );
      this.cu_name = this.populateTextTrim(details['cu_name']);
      this.cu_sname = this.populateTextTrim(details['cu_sname']);
      this.selectedStatus = this.populateObject(this.StatusData, 'Customer Status', details['Status']);
      if (details['StatusValidFr']) {
        const [year, month, day] = details['StatusValidFr'].split('-').map((datePart) => parseInt(datePart));
        this.StatusValidFr = new Date(year, month - 1, day);
      } else {
        this.StatusValidFr = null;
      }

      this.statusHistoryData =
        details['CustomerStatus'] ?
          Array.isArray(details['CustomerStatus']) ?
            details['CustomerStatus']
          : [details['CustomerStatus']]
        : [];
      this.statusHistoryData = this.statusHistoryData.map((data) => {
        data['Valid From'] = data['Valid From']?.split('T')?.[0] ?? '';
        data['Valid To'] = data['Valid To']?.split('T')?.[0] ?? '';
        return data;
      });

      this.Cus_RegNo = this.populateTextTrim(details['Cus_RegNo']);
      this.Subsidiary = this.populateTextTrim(details['Subsidiary']);
      this.addressData['addressLine1'] = this.populateTextTrim(details['cu_addr1']);
      this.addressData['addressLine2'] = this.populateTextTrim(details['cu_addr2']);
      this.addressData['addressLine3'] = this.populateTextTrim(details['cu_addr3']);
      this.addressData['addressLine4'] = this.populateTextTrim(details['cu_addr4']);
      this.addressData['postcode'] = this.populateTextTrim(details['Postcode']);
      this.addressData['city'] = this.populateTextTrim(details['City']);
      this.addressData['state'] = this.populateTextTrim(details['State']);
      this.addressData['country'] = this.populateTextTrim(details['Country']);
      this.cu_phone = this.populateTextTrim(details['cu_phone']);
      this.cu_fax = this.populateTextTrim(details['cu_fax']);
      this.cu_email = this.populateTextTrim(details['cu_email']);
      this.selectedBranch = this.populateObject(this.BrCodeData, 'Branch Code', details['BrCode']);
      //#endregion

      //#region populate Contact Details tab
      this.contactPerson = this.populateTextTrim(details['cu_contact']);
      this.contacts =
        details['Contact'] ?
          Array.isArray(details['Contact']) ?
            details['Contact']
          : [details['Contact']]
        : [];
      this.contacts.forEach((data) => {
        data['DateUpdate'] = data['DateUpdate'] ? new Date(data['DateUpdate']).formatString('dd/MM/yyyy HH:mm') : null;
      });
      this.originalContact = [...this.contacts];
      //#endregion

      //#region populate Billing Info tab
      this.selectedCustType = this.populateObject(this.custTypeButton, 'keyValue', details['CustType']);
      this.billingCustCodeDisable = Boolean(this.selectedCustType['keyValue'] != '1');
      this.selectedCustCode = this.populateObject(this.formattedCustCodeData, 'Customer Code', details['cu_BillTo']);
      if (!this.selectedCustCode) {
        this.selectedCustCode = null;
        this.customerCodeSearch = details['cu_BillTo'];
      }
      this.selectedDefaultPayType = this.populateObject(
        this.formattedPaymentTypeData,
        'Payment Type',
        details['DefaultPayType'],
      );
      this.BillingTerm = this.populateTextTrim(details['BillingTerm']);
      this.selectedBillingType = this.populateObject(this.BillingTypeData, 'BillType', details['BillingType']);
      this.selectedBillingFmt = this.populateObject(this.formattedBillFormat, 'Billing Fmt', details['BillingFmt']);
      this.selectedDefaultCur = this.populateObject(
        this.formattedDefaultCurrencyData,
        'Currency Code',
        details['DefaultCur'],
      );
      this.selectedCu_GLAcc = this.populateObject(this.formattedGLAccCodeData, 'Acc No', details['Cu_GLAcc']);
      this.CDAPrintPrice = details['CDAPrintPrice'] == '1';
      this.selectedInternalCust = this.populateObject(
        this.InternalCustData,
        'InternalCustType',
        details['InternalCust'],
      );
      this.selectedDepotStorageBillType = this.populateObject(
        this.DepotStorageBillTypeData,
        'DepotStorageBillType',
        details['DepotStorageBillType'],
      );
      this.TrDetFreeDaysOrHrs = this.populateTextTrim(details['TrDetFreeDaysOrHrs']);
      this.TrkStgFreeDaysOrHrs = this.populateTextTrim(details['TrkStgFreeDaysOrHrs']);
      this.billingScheduleCode = this.populateTextTrim(details['abBillingCategory']);
      this.selectedabInvoiceOption = this.populateObject(
        this.abInvoiceOptionData,
        'InvoiceOption',
        details['abInvoiceOption'],
      );
      //#endregion

      //#region populate Credit Management tab
      this.selectedCreditController = this.populateObject(
        this.CreditControllerData,
        'EmployeeID',
        details['CreditController'],
      );
      this.CreditLimit = this.populateTextTrim(details['CreditLimit']);
      this.CreditTerm = this.populateTextTrim(details['CreditTerm']);
      this.selectedCreditType = this.populateObject(this.CreditTypeData, 'Credit Type', details['CreditType']);
      this.CrRating = this.populateTextTrim(details['CrRating']);
      this.cu_overpay = this.populateTextTrim(details['cu_overpay']);
      this.cu_crdlmt = this.populateTextTrim(details['cu_crdlmt']);
      this.CurrentOutStanding = this.populateTextTrim(details['CurrentOutStanding']);
      //this.selectedApprovedBy = this.populateObject(this.ApprovedByData, 'EmployeeID', details['ExtendedCredit']['ApprovedBy']);

      this.selectedApprovedBy =
        details['ExtendedCredit'] ?
          this.populateObject(this.ApprovedByData, 'EmployeeID', details['ExtendedCredit']['ApprovedBy'])
        : null;
      this.ExtCreditLimit =
        details['ExtendedCredit'] ? this.populateTextTrim(details['ExtendedCredit']['ExtCreditLimit']) : '';
      this.ExtCreditTerm =
        details['ExtendedCredit'] ? this.populateTextTrim(details['ExtendedCredit']['ExtCreditTerm']) : '';
      this.ValidFr = details['ExtendedCredit'] ? new Date(details['ExtendedCredit']['ValidFr']) : null;
      this.ValidTo = details['ExtendedCredit'] ? new Date(details['ExtendedCredit']['ValidTo']) : null;

      this.AuditExtendedCreditData = []; // issue with TableBox, old data will still be displayed even though they have been removed from the array
      await this.$nextTick();

      this.AuditExtendedCreditData =
        details['AuditExtendedCredit'] ?
          Array.isArray(details['AuditExtendedCredit']) ?
            details['AuditExtendedCredit']
          : [details['AuditExtendedCredit']]
        : [];
      this.AuditExtendedCreditData.forEach((data) => {
        data['ValidFrom'] = data['ValidFrom'] ? new Date(data['ValidFrom']).formatString('dd/MM/yyyy HH:mm') : null;
        data['ValidTo'] = data['ValidTo'] ? new Date(data['ValidTo']).formatString('dd/MM/yyyy HH:mm') : null;
        data['DateUpdate'] = data['DateUpdate'] ? new Date(data['DateUpdate']).formatString('dd/MM/yyyy HH:mm') : null;
      });
      // #endregion

      //#region populate Sales & Service Team tab
      this.SalesServiceData =
        details['SalesService'] ?
          Array.isArray(details['SalesService']) ?
            details['SalesService']
          : [details['SalesService']]
        : [];
      this.SalesServiceData.forEach((data) => {
        data['DateUpdated'] =
          data['DateUpdated'] ? new Date(data['DateUpdated']).formatString('dd/MM/yyyy HH:mm') : null;
      });
      this.SalesServiceData = this.SalesServiceData;
      this.SalesServiceData.forEach((s) => this.originalSalesService.push(s));
      //#endregion

      //#region populate Attachments tab
      this.attachmentsData = attachments;
      this.attachmentsData.forEach((a) => this.originalAttachments.push(a));
      //#endregion

      //#region populate GST/SST/eInvoice tab
      this.gstData['comRegNo'] = details['GST'] ? this.populateTextTrim(details['GST']?.['GST2CoRegNo']) : '';
      this.gstData['dateVerified'] =
        details['GST']?.['GST2DateStatusVerified'] ? new Date(details['GST']['GST2DateStatusVerified']) : null;
      this.gstData['licenseNo'] = details['GST'] ? this.populateTextTrim(details['GST']['GST2LicenseNo']) : '';
      this.gstData['rmcdApprovalNo'] =
        details['GST'] ? this.populateTextTrim(details['GST']['GST2RMCDApprovalNo']) : '';
      this.gstData['selectedTaxCode'] =
        details['GST'] ?
          this.populateObject(this.gstData['taxCodeItems'], 'Tax Code', details['GST']['GST2TaxCode'])
        : null;
      this.SSTLicenseNo = details['GST'] ? this.populateTextTrim(details['GST']['GSTLicenseNo']) : '';
      this.eInvoiceTIN = details['GST'] ? this.populateTextTrim(details['GST']['TIN']) : '';
      this.TaxExemptData =
        details['TaxExempt'] ?
          Array.isArray(details['TaxExempt']) ?
            details['TaxExempt']
          : [details['TaxExempt']]
        : [];
      this.TaxExemptData.forEach((data) => {
        data['DateUpdated'] =
          data['DateUpdated'] ? new Date(data['DateUpdated']).formatString('dd/MM/yyyy HH:mm') : null;
      });
      this.originalTaxExempt = [...this.TaxExemptData];
      //#endregion

      this.$fullscreenLoading();
    },
    clearTabFields: function () {
      this.viewMode = false;

      this.currentTab = 0;

      this.customerCode = '';
      this.customerCodeSearch = '';
      this.customerCodeError = '';
      //this.isActive = false;

      this.lastUpdate = '';

      //#region General Details data
      this.custTypeRequired = false;

      this.selectedCustType =
        this.DefaultGLAccData['master_hidecustboth'] != '1' ?
          this.populateObject(this.custTypeButton, 'keyValue', '0')
        : this.populateObject(this.custTypeButton, 'keyValue', '2');
      this.billingCustCodeDisable = Boolean(
        this.DefaultGLAccData['master_hidecustboth'] != '1' || this.selectedCustType['keyValue'] != '1',
      );
      this.selectedCustCategory = null;
      this.categoryError = '';
      this.cu_name = '';
      this.nameError = '';
      this.cu_sname = '';
      this.snameError = '';
      this.selectedStatus = this.populateObject(this.StatusData, 'Description', 'ACTIVE');
      this.statusError = '';
      this.StatusValidFr = new Date();
      this.StatusValidFrError = '';
      this.showStatusHistory = false;
      this.statusHistoryData = [];
      this.Cus_RegNo = '';
      this.regNoError = '';
      this.Subsidiary = '';
      this.subsidiaryError = '';
      this.addressData['addressLine1'] = '';
      this.addressData['addressLine2'] = '';
      this.addressData['addressLine3'] = '';
      this.addressData['addressLine4'] = '';
      this.addressData['postcode'] = '';
      this.addressData['city'] = '';
      this.addressData['state'] = '';
      this.addressData['country'] = '';
      this.addressErrors['addressLine1'] = '';
      this.addressErrors['addressLine2'] = '';
      this.addressErrors['addressLine3'] = '';
      this.addressErrors['addressLine4'] = '';
      this.addressErrors['city'] = '';
      this.cu_phone = '';
      this.phoneError = '';
      this.cu_fax = '';
      this.faxError = '';
      this.cu_email = '';
      this.emailError = '';
      this.selectedBranch = null;
      //#endregion

      //#region Contact Details data
      this.contactPerson = '';
      this.contactPersonError = '';
      this.contacts = [];
      this.originalContact = [];
      this.contactShowInputs = false;
      //#endregion

      //#region Billing Info data
      this.selectedCustCode = null;
      this.custCodeError = '';
      this.selectedDefaultPayType = null;
      this.BillingTerm = '';
      this.billingTermError = '';
      this.selectedBillingType = null;
      this.selectedBillingFmt = null;
      this.selectedDefaultCur = null;
      this.selectedCu_GLAcc = this.populateObject(this.Cu_GLAccData, 'Acc No', this.DefaultGLAccData['debtorctrlacc']);
      this.glAccCodeError = '';
      this.CDAPrintPrice = false;
      this.selectedInternalCust = null;
      this.selectedDepotStorageBillType = null;
      this.TrDetFreeDaysOrHrs = '';
      this.TrDetFreeDaysOrHrsError = '';
      this.TrkStgFreeDaysOrHrs = '';
      this.TrkStgFreeDaysOrHrsError = '';
      this.billingScheduleCode = '';
      this.selectedabInvoiceOption = null;

      this.billingScheduleShow = false;
      this.clearAllBillingScheduleFieldAndRadio();
      //#endregion

      //#region Credit Management data
      this.selectedCreditController = null;
      this.CreditLimit = '';
      this.creditLimitError = '';
      this.CreditTerm = '';
      this.creditTermError = '';
      this.selectedCreditType = null;
      this.CrRating = '';
      this.crRatingError = '';
      this.cu_overpay = '';
      this.cu_overpayError = '';
      this.cu_crdlmt = '';
      this.cu_crdlmtError = '';
      this.CurrentOutStanding = '';

      this.extCreditEnabled = false;
      this.selectedApprovedBy = null;
      this.ExtCreditLimit = '';
      this.extCreditLimitError = '';
      this.ExtCreditTerm = '';
      this.extCreditTermError = '';
      this.ValidFr = null;
      this.ValidTo = null;

      this.AuditExtendedCreditData = [];
      //#endregion

      //#region Sales & Service Team data
      this.showCustSalesDetails = false;
      this.SalesServiceData = [];
      this.originalSalesService = [];
      this.selectedSalesService = [];

      this.clearSalesServiceTeamsField();
      //#endregion

      //#region Attachments data
      this.attachmentsData = [];
      this.originalAttachments = [];
      //#endregion

      //#region GST/SST/eInvoice data
      this.gstData['comRegNo'] = '';
      this.gstData['dateVerified'] = null;
      this.gstData['licenseNo'] = '';
      this.gstData['licenseNoError'] = '';
      this.gstData['rmcdApprovalNo'] = '';
      this.gstData['rmcdApprovalNoError'] = '';
      this.gstData['selectedTaxCode'] = null;
      this.SSTLicenseNo = '';
      this.eInvoiceTIN = '';
      this.validateTINIcon = '';
      this.validateTINMsg = 'Not Validated';

      this.showCustTaxExempt = false;
      this.TaxExemptData = [];
      this.originalTaxExempt = [];
      this.selectedTaxRecord = [];

      this.clearTaxExemptRecordField();
      //#endregion
    },
    tabClicked: async function (_tabItem, tabIndex) {
      if (this.contactShowInputs && tabIndex !== 1) {
        const discardChanges = await this.promptDiscardContactDetailsChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.contactShowInputs = false;
      }

      if (this.billingScheduleShow && tabIndex !== 2) {
        const discardChanges = await this.promptDiscardBillingScheduleChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.billingScheduleShow = false;
        this.clearAllBillingScheduleFieldAndRadio();
      }

      if (this.extCreditEnabled && tabIndex !== 3) {
        const discardChanges = await this.promptDiscardExtendedCreditChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.cancelExtCredFields();
      }

      if (this.showCustSalesDetails && tabIndex !== 4) {
        const discardChanges = await this.promptDiscardSalesAndServiceTeamChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.showCustSalesDetails = false;
        this.clearSalesServiceTeamsField();
        this.selectedSalesService = [];
      }

      if (this.showCustTaxExempt && tabIndex !== 6) {
        const discardChanges = await this.promptDiscardTaxExemptChanges();
        if (discardChanges === 'Cancel') {
          return;
        }
        this.showCustTaxExempt = false;
        this.clearTaxExemptRecordField();
        this.selectedTaxRecord = [];
      }
      this.currentTab = tabIndex;
    },

    clearEntryMessages: function () {
      this.entryRequired = [];
      this.entrySuccess = [];
      this.entryMessages = [];
      this.entryError = [];
    },
    entryRequiredClicked: function (item) {
      let errorTabs = this.errorMapping.find((e) => e['key'] == item);

      if (this.currentTab != errorTabs['tab'] && errorTabs['tab'] >= 0) {
        this.currentTab = errorTabs['tab'];
      }

      this.$nextTick(() => {
        switch (errorTabs['key']) {
          case 'cu_addressline1':
            this.$refs['addressForm'].setFocus('address1');
            break;

          case 'cu_addressline2':
            this.$refs['addressForm'].setFocus('address2');
            break;

          case 'cu_addressline3':
            this.$refs['addressForm'].setFocus('address3');
            break;

          case 'cu_addressline4':
            this.$refs['addressForm'].setFocus('address4');
            break;

          case 'cu_city':
            this.$refs['addressForm'].setFocus('city');
            break;

          case 'cu_contact':
            this.$refs['contactTab'].focusContactPersonInput();
            break;

          case 'gst_licenseno':
            this.$refs['gstTab'].focusGSTInput('licenseno');
            break;

          case 'gst_rmcdapprovalno':
            this.$refs['gstTab'].focusGSTInput('rmcdapprovalno');
            break;

          default:
            this.$refs[item].setFocus();
            break;
        }
      });
    },

    populateObject: function (data, key, compare) {
      // To populate object into dropdown or input query
      try {
        return data.find((d) => d[key].trim() == (compare ? compare.trim() : ''));
      } catch (error) {
        return null;
      }
    },
    populateTextTrim: function (text) {
      // To populate text with trim
      return text ? text.trim() : '';
    },

    checkFields: function () {
      this.clearEntryMessages();

      if (!this.customerCode) {
        this.customerCodeError = 'Customer Code is required!';
        this.entryRequired.push({ key: 'customerCode', value: 'Customer Code is required!' });
      } else if (this.customerCode.length > 10) {
        this.customerCodeError = 'Customer Code cannot exceed 10 characters!';
        this.entryRequired.push({ key: 'customerCode', value: 'Customer Code cannot exceed 10 characters!' });
      }
      if (!this.selectedCustCategory) {
        this.categoryError = 'Customer Category is required!';
        this.entryRequired.push({ key: 'cu_category', value: 'Customer Category is required!' });
      }
      if (!this.cu_name) {
        this.nameError = 'Customer Name is required!';
        this.entryRequired.push({ key: 'cu_name', value: 'Customer Name is required!' });
      } else if (this.cu_name.length > 100) {
        this.nameError = 'Customer Name cannot exceed 100 characters!';
        this.entryRequired.push({ key: 'cu_name', value: 'Customer Name cannot exceed 100 characters!' });
      }
      if (!this.cu_sname) {
        this.snameError = 'Customer Short Name is required!';
        this.entryRequired.push({ key: 'cu_sname', value: 'Customer Short Name is required!' });
      } else if (this.cu_sname.length > 20) {
        this.snameError = 'Customer Short Name cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'cu_sname', value: 'Customer Short Name cannot exceed 20 characters!' });
      }
      if (!this.selectedStatus) {
        this.statusError = 'Customer Status is required!';
        this.entryRequired.push({ key: 'cu_status', value: 'Customer Status is required!' });
      }
      if (!this.StatusValidFr) {
        this.StatusValidFrError = 'Status From is required!';
        this.entryRequired.push({ key: 'cu_statusfr', value: 'Status From is required!' });
      }
      if (this.Cus_RegNo.length > 20) {
        this.regNoError = 'Comp Reg. No cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'cu_regno', value: 'Comp Reg. No cannot exceed 20 characters!' });
      }
      if (this.Subsidiary.length > 10) {
        this.subsidiaryError = 'Subsidiary of cannot exceed 10 characters!';
        this.entryRequired.push({ key: 'cu_subsidiary', value: 'Subsidiary of cannot exceed 10 characters!' });
      }
      if (this.addressData.addressLine1.length > 40) {
        this.addressErrors.addressLine1 = 'Address Line 1 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'cu_addressline1', value: 'Address Line 1 cannot exceed 40 characters!' });
      }
      if (this.addressData.addressLine2.length > 40) {
        this.addressErrors.addressLine2 = 'Address Line 2 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'cu_addressline2', value: 'Address Line 2 cannot exceed 40 characters!' });
      }
      if (this.addressData.addressLine3.length > 40) {
        this.addressErrors.addressLine3 = 'Address Line 3 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'cu_addressline3', value: 'Address Line 3 cannot exceed 40 characters!' });
      }
      if (this.addressData.addressLine4.length > 40) {
        this.addressErrors.addressLine4 = 'Address Line 4 cannot exceed 40 characters!';
        this.entryRequired.push({ key: 'cu_addressline4', value: 'Address Line 4 cannot exceed 40 characters!' });
      }
      if (this.addressData.city.length > 30) {
        this.addressErrors.city = 'City cannot exceed 30 characters!';
        this.entryRequired.push({ key: 'cu_city', value: 'City cannot exceed 30 characters!' });
      }
      if (this.cu_phone.length > 20) {
        this.phoneError = 'Phone no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'cu_phone', value: 'Phone no cannot exceed 20 characters!' });
      }
      if (this.cu_fax.length > 20) {
        this.faxError = 'Fax no cannot exceed 20 characters!';
        this.entryRequired.push({ key: 'cu_fax', value: 'Fax no cannot exceed 20 characters!' });
      }
      if (this.cu_email.length > 100) {
        this.emailError = 'Email cannot exceed 100 characters!';
        this.entryRequired.push({ key: 'cu_email', value: 'Email cannot exceed 100 characters!' });
      }

      if (this.contactPerson.length > 100) {
        this.contactPersonError = 'Contact Person cannot exceed 100 characters!';
        this.entryRequired.push({ key: 'cu_contact', value: 'Contact Person cannot exceed 100 characters!' });
      }

      if (!this.selectedCustCode && this.selectedCustType['keyValue'] == '1') {
        this.custCodeError = 'Billing Customer Code is required!';
        this.entryRequired.push({ key: 'cu_billcustcode', value: 'Billing Customer Code is required!' });
      }
      if (this.BillingTerm.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.billingTermError = 'Billing term cannot exceed 9 digits!';
        this.entryRequired.push({ key: 'cu_billingterm', value: 'Billing term cannot exceed 9 digits!' });
      }
      if (!this.selectedCu_GLAcc) {
        this.glAccCodeError = 'Billing GL Acc Code is required!';
        this.entryRequired.push({ key: 'cu_glacccode', value: 'Billing GL Acc Code is required!' });
      }
      if (this.TrDetFreeDaysOrHrs.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.TrDetFreeDaysOrHrsError = 'Trailer Detention cannot exceed 9 digits!';
        this.entryRequired.push({ key: 'cu_trailerdetention', value: 'Trailer Detention cannot exceed 9 digits!' });
      }
      if (this.TrkStgFreeDaysOrHrs.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.TrkStgFreeDaysOrHrsError = 'Trailer Staging cannot exceed 9 digits!';
        this.entryRequired.push({ key: 'cu_trailerstaging', value: 'Trailer Staging cannot exceed 9 digits!' });
      }

      if (!this.CreditLimit.match(/^\d*\.?\d*$/g) && this.CreditLimit) {
        this.creditLimitError = 'Credit Limit must be a number!';
        this.entryRequired.push({ key: 'cu_creditlimit', value: 'Credit Limit must be a number!' });
      } else if (this.CreditLimit) {
        // database: money,prec=19,scale=4; 15 digits, 4 decimals on front-end
        const [whole, decimal] = this.CreditLimit.split('.');
        if (whole.length > 15) {
          this.creditLimitError = 'Credit Limit cannot exceed 15 whole digits and 4 decimals!';
          this.entryRequired.push({
            key: 'cu_creditlimit',
            value: 'Credit Limit cannot exceed 15 whole digits and 4 decimals!',
          });
        } else if (decimal?.length > 4) {
          this.creditLimitError = 'Credit Limit cannot exceed 15 whole digits and 4 decimals!';
          this.entryRequired.push({
            key: 'cu_creditlimit',
            value: 'Credit Limit cannot exceed 15 whole digits and 4 decimals!',
          });
        }
      }
      if (this.CreditTerm.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.creditTermError = 'Credit Term cannot exceed 9 digits!';
        this.entryRequired.push({ key: 'cu_creditterm', value: 'Credit Term cannot exceed 9 digits!' });
      }
      if (this.creditTypeRequired && !this.selectedCreditType) {
        this.creditTypeError = 'Credit Type is required!';
        this.entryRequired.push({ key: 'cu_credittype', value: 'Credit Type is required!' });
      }
      if (this.CrRating.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.crRatingError = 'Credit Rating cannot exceed 9 digits!';
        this.entryRequired.push({ key: 'cu_creditrating', value: 'Credit Rating cannot exceed 9 digits!' });
      }
      if (!this.cu_overpay.match(/^\d*\.?\d*$/g) && this.cu_overpay) {
        this.cu_overpayError = 'Overdraft Amount must be a number!';
        this.entryRequired.push({ key: 'cu_overdraft', value: 'Overdraft Amount must be a number!' });
      } else if (this.cu_overpay) {
        // database: money,prec=19,scale=4; 15 digits, 4 decimals on front-end
        const [whole, decimal] = this.cu_overpay.split('.');
        if (whole.length > 15) {
          this.cu_overpayError = 'Overdraft Amount cannot exceed 15 whole digits and 4 decimals!';
          this.entryRequired.push({
            key: 'cu_overdraft',
            value: 'Overdraft Amount cannot exceed 15 whole digits and 4 decimals!',
          });
        } else if (decimal?.length > 4) {
          this.cu_overpayError = 'Overdraft Amount cannot exceed 15 whole digits and 4 decimals!';
          this.entryRequired.push({
            key: 'cu_overdraft',
            value: 'Overdraft Amount cannot exceed 15 whole digits and 4 decimals!',
          });
        }
      }
      if (!this.cu_crdlmt.match(/^\d*\.?\d*$/g) && this.cu_crdlmt) {
        this.cu_crdlmtError = 'Cash Float Amount must be a number!';
        this.entryRequired.push({ key: 'cu_cashfloat', value: 'Cash Float Amount must be a number!' });
      } else if (this.cu_crdlmt) {
        // database: decimal,prec=8,scale=2; 6 digits, 2 decimals on front-end
        const [whole, decimal] = this.cu_crdlmt.split('.');
        if (whole.length > 6) {
          this.cu_crdlmtError = 'Cash Float Amount cannot exceed 6 whole digits and 2 decimals!';
          this.entryRequired.push({
            key: 'cu_cashfloat',
            value: 'Cash Float Amount cannot exceed 6 whole digits and 2 decimals!',
          });
        } else if (decimal?.length > 2) {
          this.cu_crdlmtError = 'Cash Float Amount cannot exceed 6 whole digits and 2 decimals!';
          this.entryRequired.push({
            key: 'cu_cashfloat',
            value: 'Cash Float Amount cannot exceed 6 whole digits and 2 decimals!',
          });
        }
      }

      if (this.gstData.licenseNo.length > 50) {
        this.gstData.licenseNoError = 'License No cannot exceed 50 characters!';
        this.entryRequired.push({ key: 'gst_licenseno', value: 'License No cannot exceed 50 characters!' });
      }
      if (this.gstData.rmcdApprovalNo.length > 25) {
        this.gstData.rmcdApprovalNoError = 'RMCD Approval No cannot exceed 25 characters!';
        this.entryRequired.push({ key: 'gst_rmcdapprovalno', value: 'RMCD Approval No cannot exceed 25 characters!' });
      }
      if (this.SSTLicenseNo.length > 50) {
        this.SSTLicenseNoError = 'SST License No cannot exceed 50 characters!';
        this.entryRequired.push({ key: 'SSTLicenseNo', value: 'SST License No cannot exceed 50 characters!' });
      }
      if (this.eInvoiceTIN.length > 13) {
        this.eInvoiceTINError = 'Tax Identification No (TIN) cannot exceed 13 characters!';
        this.entryRequired.push({
          key: 'eInvoiceTIN',
          value: 'Tax Identification No (TIN) cannot exceed 13 characters!',
        });
      }

      return this.entryRequired.length > 0;
    },
    saveClicked: async function () {
      if (this.contactShowInputs) {
        const discardChanges = await this.promptDiscardContactDetailsChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.contactShowInputs = false;
      }

      if (this.billingScheduleShow) {
        const discardChanges = await this.promptDiscardBillingScheduleChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.billingScheduleShow = false;
        this.clearAllBillingScheduleFieldAndRadio();
      }

      if (this.extCreditEnabled) {
        const discardChanges = await this.promptDiscardExtendedCreditChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.cancelExtCredFields();
      }

      if (this.showCustSalesDetails) {
        const discardChanges = await this.promptDiscardSalesAndServiceTeamChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showCustSalesDetails = false;
        this.clearSalesServiceTeamsField();
        this.selectedSalesService = [];
      }

      if (this.showCustTaxExempt) {
        const discardChanges = await this.promptDiscardTaxExemptChanges();
        if (discardChanges === 'Cancel') {
          return;
        }

        this.showCustTaxExempt = false;
        this.clearTaxExemptRecordField();
        this.selectedTaxRecord = [];
      }

      this.customerCode = this.customerCode.trim();
      const isNotValid = this.checkFields();
      if (isNotValid) {
        return;
      }

      if (this.statusHistoryData.length > 0) {
        const lastStatus = this.statusHistoryData.reduce((accumulator, currentStatus) => {
          // find the status with the highest #
          return accumulator['#'] < currentStatus['#'] ? currentStatus : accumulator;
        });

        const [lastStatusYear, lastStatusMonth, lastStatusDay] = lastStatus['Valid From']
          .split('-')
          .map((datePart) => parseInt(datePart));
        // expects Valid From to be in yyyy-MM-dd

        if (
          this.StatusValidFr.getFullYear() === lastStatusYear &&
          this.StatusValidFr.getMonth() + 1 === lastStatusMonth &&
          this.StatusValidFr.getDate() === lastStatusDay &&
          this.selectedStatus['Description'].trim() !== lastStatus.Status.trim()
        ) {
          const replaceConfirmation = await this.$showAlert(
            'warning',
            'Undo Previous Status',
            `Do you wish to undo the previous ${lastStatus.Status} status?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray' },
            ],
          );

          if (replaceConfirmation === 'No') {
            return;
          }
        }
      }

      let action = this.popupEntryTitle[0];

      let contactToSend = this.contacts.filter((c) => c['contactaction']);
      let salesServiceToSend = this.SalesServiceData.filter((s) => s['SalesAction']);
      let attachmentsToSend = this.attachmentsData.filter((a) => a['attachmentaction']);
      let taxExemptRecordsToSend = this.TaxExemptData.filter((t) => t['taxAction']);

      //#region For deleted contacts
      let ids = this.contacts.map((ct) => ct['ID']);
      let deleted = this.originalContact.filter((c) => !ids.includes(c['ID']));
      deleted.forEach((d) => (d['contactaction'] = 'D'));
      contactToSend = contactToSend.concat(deleted);
      //#endregion

      //#region For deleted attachments
      let attIds = this.attachmentsData.map((a) => a['ID']);
      let attDeleted = this.originalAttachments.filter((o) => !attIds.includes(o['ID']));
      attDeleted.forEach((ad) => (ad['attachmentaction'] = 'D'));
      attachmentsToSend = attachmentsToSend.concat(attDeleted);
      //#endregion

      //#region For deleted sales service
      let salesServiceJobTypes = this.SalesServiceData.map((s) => s['JobType']);
      let salesServiceSystems = this.SalesServiceData.map((s) => s['System']);
      let salesServiceDeleted = this.originalSalesService.filter(
        (o) => !(salesServiceJobTypes.includes(o['JobType']) && salesServiceSystems.includes(o['System'])),
      );
      salesServiceDeleted.forEach((sd) => (sd['SalesAction'] = 'D'));
      salesServiceToSend = salesServiceToSend.concat(salesServiceDeleted);
      //#endregion

      //#region For deleted tax exemption
      let taxExemptCharge = this.TaxExemptData.map((t) => t['ChargeCode']);
      let taxExemptDeleted = this.originalTaxExempt.filter((o) => !taxExemptCharge.includes(o['ChargeCode']));
      taxExemptDeleted.forEach((td) => (td['taxAction'] = 'D'));
      taxExemptRecordsToSend = taxExemptRecordsToSend.concat(taxExemptDeleted);
      taxExemptRecordsToSend = taxExemptRecordsToSend.map((t) => ({
        chargecode: t['ChargeCode'],
        taxcode: t['TaxCode'],
        userid: t['UserID'],
        dateupdated: t['DateUpdated'],
        taxaction: t['taxAction'],
      }));
      //#endregion

      contactToSend.forEach((c) => {
        // To make sure that if action is E, ID cannot be empty
        if (c['contactaction'] == 'E' && !c['ID']) {
          c['contactaction'] = 'N';
        }
      });

      salesServiceToSend.forEach((s) => {
        // To make sure that if action is E, UserID cannot be empty
        if (s['SalesAction'] == 'E' && !s['UserID']) {
          s['SalesAction'] = 'N';
        }
      });

      let dataToPost = {
        action: action,
        cu_number: this.customerCode,
        LastUpdate: this.lastUpdate,

        //#region from General Details tab
        CustType: this.selectedCustType?.['keyValue'] ?? '',
        cu_name: this.cu_name,
        cu_sname: this.cu_sname,
        Cus_RegNo: this.Cus_RegNo,
        Subsidiary: this.Subsidiary,
        StatusValidFr: this.StatusValidFr?.formatString('yyyy-MM-dd') ?? '',
        cu_addr1: this.addressData['addressLine1'],
        cu_addr2: this.addressData['addressLine2'],
        cu_addr3: this.addressData['addressLine3'],
        cu_addr4: this.addressData['addressLine4'],
        Postcode: this.addressData['postcode'],
        City: this.addressData['city'],
        State: this.addressData['state'],
        Country: this.addressData['country'],
        cu_phone: this.cu_phone,
        cu_fax: this.cu_fax,
        cu_email: this.cu_email,
        CustCategory: this.selectedCustCategory ? this.selectedCustCategory['Customer Category'] : '',
        Status: this.selectedStatus ? this.selectedStatus['Customer Status'] : '',
        BrCode: this.selectedBranch ? this.selectedBranch['Branch Code'] : '',
        //#endregion

        //#region from Contact Details tab
        cu_contact: this.contactPerson,
        Contact: contactToSend,
        //#endregion

        //#region from Billing Info tab
        cu_BillTo:
          this.selectedCustType['keyValue'] != '0' ?
            this.selectedCustCode ?
              this.selectedCustCode['Customer Code']
            : ''
          : this.customerCode,
        DefaultPayType: this.selectedDefaultPayType ? this.selectedDefaultPayType['Payment Type'] : '',
        BillingTerm: this.BillingTerm,
        BillingType: this.selectedBillingType ? this.selectedBillingType['BillType'] : '',
        BillingFmt: this.selectedBillingFmt ? this.selectedBillingFmt['Billing Fmt'] : '',
        DefaultCur: this.selectedDefaultCur ? this.selectedDefaultCur['Currency Code'] : '',
        Cu_GLAcc: this.selectedCu_GLAcc ? this.selectedCu_GLAcc['Acc No'] : '',
        CDAPrintPrice: this.CDAPrintPrice ? '1' : '0',
        InternalCust: this.selectedInternalCust ? this.selectedInternalCust['InternalCustType'] : '',
        DepotStorageBillType:
          this.selectedDepotStorageBillType ? this.selectedDepotStorageBillType['DepotStorageBillType'] : '',
        TrkStgFreeDaysOrHrs: this.TrkStgFreeDaysOrHrs,
        TrDetFreeDaysOrHrs: this.TrDetFreeDaysOrHrs,
        abBillingCategory: this.billingScheduleCode,
        abInvoiceOption: this.selectedabInvoiceOption ? this.selectedabInvoiceOption['InvoiceOption'] : '',
        //#endregion

        //#region from Credit Management tab
        CreditController: this.selectedCreditController ? this.selectedCreditController['EmployeeID'] : '',
        CreditLimit: this.CreditLimit,
        CreditTerm: this.CreditTerm,
        CreditType: this.selectedCreditType ? this.selectedCreditType['Credit Type'] : '',
        CrRating: this.CrRating,
        cu_overpay: this.cu_overpay,
        cu_crdlmt: this.cu_crdlmt ? this.cu_crdlmt : 0,
        CurrentOutStanding: this.CurrentOutStanding,
        // float?
        // outstanding?
        ExtApprovedBy: this.selectedApprovedBy ? this.selectedApprovedBy['EmployeeID'] : '',
        ExtCreditLimit: this.ExtCreditLimit,
        ExtCreditTerm: this.ExtCreditTerm,
        ExtCreditType: this.selectedCreditType ? this.selectedCreditType['Credit Type'] : '', // should always be the same as Credit Type
        ExtValidFr: this.ValidFr ? this.ValidFr.formatString('yyyy-MM-dd HH:mm') : '',
        ExtValidTo: this.ValidTo ? this.ValidTo.formatString('yyyy-MM-dd HH:mm') : '',
        //#endregion

        //#region from Sales & Service Team tab
        SalesService: salesServiceToSend,
        //#endregion

        //#region from Attachments tab
        Attachment: attachmentsToSend,
        //#endregion

        //#region from GST/SST/eInvoice tab
        GST: {
          GST2CoRegNo: this.gstData['comRegNo'],
          GST2DateStatusVerified:
            this.gstData['dateVerified'] && Number.isNaN(this.gstData['dateVerified']) == false ?
              this.gstData['dateVerified'].formatString('yyyy-MM-dd HH:mm')
            : '',
          GST2LicenseNo: this.gstData['licenseNo'],
          GST2RMCDApprovalNo: this.gstData['rmcdApprovalNo'],
          GST2TaxCode: this.gstData['selectedTaxCode'] ? this.gstData['selectedTaxCode']['Tax Code'] : '',
          GSTLicenseNo: this.SSTLicenseNo,
          TIN: this.eInvoiceTIN,
        },
        TaxExempt: taxExemptRecordsToSend,
        //#endregion
      };

      this.$fullscreenLoading('Saving Customer');
      let result = await this.$apiCallV2('/CustomerProfile/SaveCustomer', dataToPost);
      this.$fullscreenLoading();

      if (result[0]['Results'] == 0) {
        // Save successful
        if (action == 'N') {
          this.entrySuccess.push(`Successfully created customer ${result[0]['cu_number']}`);
        } else {
          this.entrySuccess.push(`Successfully edited customer ${result[0]['cu_number']}`);
        }

        this.selectedCustomer = [];
        this.customerList = [];
        this.viewMode = true;
        this.tableLoading = true;
        await this.populateCustomerDetails(this.customerCode);
        this.tableLoading = false;
        this.$nextTick(async () => {
          await this.getCustomers();
        });
      } else {
        // Save error
        if (action == 'N') {
          this.entryError.push(`There was an error creating your new customer: ${result[0]['ErrMsg']}`);
        } else {
          this.entryError.push(`There was an error editting customer: ${result[0]['ErrMsg']}`);
        }
      }
    },

    //#region General Details methods
    toggleStatusHistory: function (pointerEvent) {
      this.showStatusHistory = !this.showStatusHistory;
    },
    //#endregion

    //#region Contact Details methods
    promptDiscardContactDetailsChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved contact details changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    //#endregion

    //#region Billing Info methods
    changeBillingScheduleClicked: async function () {
      this.billingScheduleShow = true; // need to show inputs first
      await this.$nextTick(); // wait for inputs to render, otherwise it won't be able to select the radio buttons

      switch (
        this.billingScheduleCode.substring(0, 1) // first character
      ) {
        case '':
          this.$refs.billingScheduleNoneRadioButton.selectRadio(); // need to manually select the radio button with a ref, there's no synced prop
          this.selectedBillingSchedule = 'None'; // manually update as well, otherwise the computed properties won't update and the appropriate box won't be enabled, selecting the radio button doesn't fire the radioChanged event
          break;

        case 'D':
          this.$refs.billingScheduleDailyRadioButton.selectRadio(); // need to manually select the radio button with a ref, there's no synced prop
          this.selectedBillingSchedule = 'Daily'; // manually update as well, otherwise the computed properties won't update and the appropriate box won't be enabled, selecting the radio button doesn't fire the radioChanged event
          break;

        case 'W':
          this.$refs.billingScheduleWeeklyRadioButton.selectRadio(); // need to manually select the radio button with a ref, there's no synced prop
          this.selectedBillingSchedule = 'Weekly'; // manually update as well, otherwise the computed properties won't update and the appropriate box won't be enabled, selecting the radio button doesn't fire the radioChanged event

          let daysSelected = this.billingScheduleCode.substring(1).split('0'); // from 2nd character onwards till the end of the string, split on "0"
          daysSelected.splice(0, 1); // remove the first element, which should always be an empty string
          for (const day of daysSelected) {
            // loop over the selected days
            switch (
              day // update the checkbox states
            ) {
              case '1':
                this.weeklyBillingScheduleMondayChecked = true;
                break;

              case '2':
                this.weeklyBillingScheduleTuesdayChecked = true;
                break;

              case '3':
                this.weeklyBillingScheduleWednesdayChecked = true;
                break;

              case '4':
                this.weeklyBillingScheduleThursdayChecked = true;
                break;

              case '5':
                this.weeklyBillingScheduleFridayChecked = true;
                break;

              case '6':
                this.weeklyBillingScheduleSaturdayChecked = true;
                break;

              case '7':
                this.weeklyBillingScheduleSundayChecked = true;
                break;
            }
          }
          break;

        case 'M':
          this.$refs.billingScheduleMonthlyRadioButton.selectRadio(); // need to manually select the radio button with a ref, there's no synced prop
          this.selectedBillingSchedule = 'Monthly'; // manually update as well, otherwise the computed properties won't update and the appropriate box won't be enabled, selecting the radio button doesn't fire the radioChanged event

          const day1 = this.billingScheduleCode.substring(1, 3); // 2nd and 3rd characers
          const day2 = this.billingScheduleCode.substring(3); // 4th and 5th chracters
          this.selectedMonthlyDay1 = this.daysData.find((d) => d['CodeValue'] === day1);
          this.selectedMonthlyDay2 = this.daysData.find((d) => d['CodeValue'] === day2);
          break;

        case 'Q':
          this.$refs.billingScheduleQuarterlyRadioButton.selectRadio(); // need to manually select the radio button with a ref, there's no synced prop
          this.selectedBillingSchedule = 'Quarterly'; // manually update as well, otherwise the computed properties won't update and the appropriate box won't be enabled, selecting the radio button doesn't fire the radioChanged event

          const quarterMonth = this.billingScheduleCode.substring(1, 3); // 2nd and 3rd characers, should only ever be "01", "02" or "03"
          const quarterDay = this.billingScheduleCode.substring(3); // 4th and 5th chracters
          this.selectedQuarterMonth = this.quarterData.find((q) => q['CodeValue'] === quarterMonth);
          this.selectedQuarterDay = this.daysData.find((d) => d['CodeValue'] == quarterDay);
          break;
      }
    },
    cancelBillingSchedule: function () {
      this.billingScheduleShow = false;
      this.clearAllBillingScheduleFieldAndRadio();
    },
    saveBillingSchedule: function () {
      let error = false; // check required fields first
      switch (
        this.selectedBillingSchedule // required fields depends on which billing schedule is selected
      ) {
        case 'Weekly':
          if (
            !this.weeklyBillingScheduleMondayChecked &&
            !this.weeklyBillingScheduleTuesdayChecked &&
            !this.weeklyBillingScheduleWednesdayChecked &&
            !this.weeklyBillingScheduleThursdayChecked &&
            !this.weeklyBillingScheduleFridayChecked &&
            !this.weeklyBillingScheduleSaturdayChecked &&
            !this.weeklyBillingScheduleSundayChecked
          ) {
            this.weeklyBillingScheduleDaysSelectedError = 'Please choose at least 1 day';
            error = true;
          }
          break;

        case 'Monthly':
          if (!this.selectedMonthlyDay1) {
            this.monthlyDay1Error = 'Please select day 1';
            error = true;
          }
          if (!this.selectedMonthlyDay2) {
            this.monthlyDay2Error = 'Please select day 2';
            error = true;
          }
          break;

        case 'Quarterly':
          if (!this.selectedQuarterMonth) {
            this.quarterMonthError = 'Please select the months in the quarter';
            error = true;
          }
          if (!this.selectedQuarterDay) {
            this.quarterDayError = 'Please select day of the month';
            error = true;
          }
          break;
      }
      if (error) {
        // if any of the required fields are missing
        return;
      }

      switch (
        this.selectedBillingSchedule // if all required fields are filled in, populate billingScheduleCode
      ) {
        case 'None':
          this.billingScheduleCode = '';
          break;

        case 'Daily':
          this.billingScheduleCode = 'D';
          break;

        case 'Weekly':
          this.billingScheduleCode = 'W';
          if (this.weeklyBillingScheduleMondayChecked) {
            this.billingScheduleCode += '01';
          }
          if (this.weeklyBillingScheduleTuesdayChecked) {
            this.billingScheduleCode += '02';
          }
          if (this.weeklyBillingScheduleWednesdayChecked) {
            this.billingScheduleCode += '03';
          }
          if (this.weeklyBillingScheduleThursdayChecked) {
            this.billingScheduleCode += '04';
          }
          if (this.weeklyBillingScheduleFridayChecked) {
            this.billingScheduleCode += '05';
          }
          if (this.weeklyBillingScheduleSaturdayChecked) {
            this.billingScheduleCode += '06';
          }
          if (this.weeklyBillingScheduleSundayChecked) {
            this.billingScheduleCode += '07';
          }
          break;

        case 'Monthly':
          this.billingScheduleCode = `M${this.selectedMonthlyDay1['CodeValue']}${this.selectedMonthlyDay2['CodeValue']}`;
          break;

        case 'Quarterly':
          this.billingScheduleCode = `Q${this.selectedQuarterMonth['CodeValue']}${this.selectedQuarterDay['CodeValue']}`;
          break;
      }

      this.billingScheduleShow = false;
      this.clearAllBillingScheduleFieldAndRadio();
    },
    scheduleSelectionRadioChanged: function (label, event) {
      switch (
        this.selectedBillingSchedule // the previous selected billing schedule
      ) {
        case 'Weekly':
          this.clearWeeklyBillingScheduleField();
          break;

        case 'Monthly':
          this.clearMonthlyBillingScheduleField();
          break;

        case 'Quarterly':
          this.clearQuarterlyBillingScheduleField();
          break;
      }

      this.selectedBillingSchedule = label; // change billing schedule to the newly selected radio button
    },
    weeklyBillingScheduleCheckboxClicked: function () {
      this.weeklyBillingScheduleDaysSelectedError = '';
    },
    clearWeeklyBillingScheduleField: function () {
      this.weeklyBillingScheduleMondayChecked = false;
      this.weeklyBillingScheduleTuesdayChecked = false;
      this.weeklyBillingScheduleWednesdayChecked = false;
      this.weeklyBillingScheduleThursdayChecked = false;
      this.weeklyBillingScheduleFridayChecked = false;
      this.weeklyBillingScheduleSaturdayChecked = false;
      this.weeklyBillingScheduleSundayChecked = false;
      this.weeklyBillingScheduleDaysSelectedError = '';
    },
    clearMonthlyBillingScheduleField: function () {
      this.selectedMonthlyDay1 = null;
      this.monthlyDay1Error = '';
      this.selectedMonthlyDay2 = null;
      this.monthlyDay2Error = '';
    },
    clearQuarterlyBillingScheduleField: function () {
      this.selectedQuarterMonth = null;
      this.quarterMonthError = '';
      this.selectedQuarterDay = null;
      this.quarterDayError = '';
    },
    clearAllBillingScheduleFieldAndRadio: function () {
      this.$refs.billingScheduleNoneRadioButton?.clearRadio?.(); // try to manually clear the radio button with a ref, there's no synced prop
      this.$refs.billingScheduleDailyRadioButton?.clearRadio?.(); // these may fail depending on whether the components are rendered or not
      this.$refs.billingScheduleWeeklyRadioButton?.clearRadio?.(); // use optional chaining to avoid errors
      this.$refs.billingScheduleMonthlyRadioButton?.clearRadio?.();
      this.$refs.billingScheduleQuarterlyRadioButton?.clearRadio?.();

      // none and daily billing schedules don't have any fields to be cleared
      this.clearWeeklyBillingScheduleField();
      this.clearMonthlyBillingScheduleField();
      this.clearQuarterlyBillingScheduleField();
    },
    promptDiscardBillingScheduleChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved billing schedule changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    //#endregion

    //#region Credit Management methods
    enableExtCredFields: function () {
      this.extCreditEnabled = true;
      this.tempSelectedApprovedBy = this.selectedApprovedBy;
      this.tempExtCreditLimit = this.ExtCreditLimit;
      this.tempExtCreditTerm = this.ExtCreditTerm;
      this.tempValidFr = this.ValidFr;
      this.tempValidTo = this.ValidTo;
    },
    applyExtCredFields: function () {
      if (this.checkExtCredFields()) {
        return;
      }

      this.extCreditEnabled = false;
    },
    cancelExtCredFields: function () {
      this.extCreditEnabled = false;
      this.selectedApprovedBy = this.tempSelectedApprovedBy;
      this.approvedByError = '';
      this.ExtCreditLimit = this.tempExtCreditLimit;
      this.extCreditLimitError = '';
      this.ExtCreditTerm = this.tempExtCreditTerm;
      this.extCreditTermError = '';
      this.ValidFr = this.tempValidFr;
      this.ValidFrError = '';
      this.ValidTo = this.tempValidTo;
      this.ValidToError = '';
    },
    checkExtCredFields: function () {
      let error = false;

      if (!this.selectedApprovedBy) {
        this.approvedByError = 'Approved By is required!';
        error = true;
      }
      if (!this.ExtCreditLimit) {
        this.extCreditLimitError = 'Extended Credit Limit is required!';
        error = true;
      } else if (!this.ExtCreditLimit.match(/^\d*\.?\d*$/g)) {
        this.extCreditLimitError = 'Extended Credit Limit must be a number!';
        error = true;
      } else {
        // database: money,prec=19,scale=4; 15 digits, 4 decimals on front-end
        const [whole, decimal] = this.ExtCreditLimit.split('.');
        if (whole.length > 15) {
          this.extCreditLimitError = 'Extended Credit Limit cannot exceed 15 whole digits!';
          error = true;
        } else if (decimal?.length > 4) {
          this.extCreditLimitError = 'Extended Credit Limit cannot exceed 4 decimals!';
          error = true;
        }
      }
      if (!this.ExtCreditTerm) {
        this.extCreditTermError = 'Extended Credit Term is required!';
      } else if (this.ExtCreditTerm.length > 9) {
        // database: int,prec=10,scale=0; 9 digits on front-end
        this.extCreditTermError = 'Extended Credit Term cannot exceed 9 digits!';
        error = true;
      }
      if (!this.selectedCreditType) {
        this.creditTypeError = 'Credit Type is required!'; // set error message credit type instead, because setting an error message on a disabled input field makes it unclearable
        error = true;
      }
      if (!this.ValidFr) {
        this.ValidFrError = 'Valid From is required!';
        error = true;
      }
      if (!this.ValidTo) {
        this.ValidToError = 'Valid To is required!';
        error = true;
      }
      if (this.ValidFr && this.ValidTo && this.ValidFr > this.ValidTo) {
        this.ValidFrError = 'Valid From date should be before Valid To date';
        error = true;
      }

      return error;
    },
    promptDiscardExtendedCreditChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved extended credit changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    //#endregion

    //#region Sales & Service Team methods
    addCustSales: function () {
      this.showCustSalesDetails = true;
    },
    deleteCustSales: function () {
      if (this.selectedSalesService.length == 0) {
        this.$showAlert('error', 'No Team selected', 'Please select at least 1 team to delete');
        return;
      }

      let temp = this.SalesServiceData;
      this.SalesServiceData = [];

      this.selectedSalesService.forEach((t) => {
        let index = temp.findIndex((s) => s['JobType'] === t['JobType'] && s['System'] === t['System']);
        temp.splice(index, 1);
      });

      this.$nextTick(() => {
        this.SalesServiceData = temp;
        this.selectedSalesService = [];
      });
    },
    salesServiceTableBoxButtonClicked: function (btn, data) {
      if (this.viewMode) return;

      this.editSalesServiceIndex = this.SalesServiceData.findIndex(
        (s) => s['JobType'] === data['JobType'] && s['System'] === data['System'],
      );
      this.Default = data['DefaultCust'] == 'Y';
      this.selectedSystem = this.SystemData.find(
        (d) => d['System'].toLowerCase().trim() == (data['System'] ? data['System'].toLowerCase().trim() : null),
      );
      this.selectedJobType = this.JobTypeData.find(
        (d) => d['JobType'].toLowerCase().trim() == (data['JobType'] ? data['JobType'].toLowerCase().trim() : null),
      );
      this.selectedSalesTeam = this.SalesTeamData.find(
        (d) =>
          d['Sales Team Code'].toLowerCase().trim() ==
          (data['SalesTeam_Code'] ? data['SalesTeam_Code'].toLowerCase().trim() : null),
      );
      this.SalesPerson = data['DefaultSalesPerson'];
      this.selectedServiceTeam = this.ServiceTeamData.find(
        (d) =>
          d['Service Team Code'].toLowerCase().trim() ==
          (data['ServiceTeam_Code'] ? data['ServiceTeam_Code'].toLowerCase().trim() : null),
      );
      this.ServicePerson = data['DefaultServicePerson'];

      this.showCustSalesDetails = true;
    },
    checkTeamFields: function () {
      let error = false;
      if (!this.selectedSystem) {
        this.systemError = 'System is required!';
        error = true;
      }
      if (!this.selectedJobType) {
        this.jobTypeError = 'Job Type is required!';
        error = true;
      }
      if (this.SalesPerson.length > 10) {
        this.salesPersonError = 'Sales Person cannot exceed 10 characters!';
        error = true;
      }
      if (this.ServicePerson.length > 10) {
        this.servicePersonError = 'Service Person cannot exceed 10 characters!';
        error = true;
      }
      if (this.selectedSalesTeam && !this.SalesPerson.trim()) {
        this.salesPersonError = 'Please specify Sales Person for selected Sales Team!';
        error = true;
      }
      if (this.selectedServiceTeam && !this.ServicePerson.trim()) {
        this.servicePersonError = 'Please specify Service Person for selected Service Team!';
        error = true;
      }
      return error;
    },
    focusCustomerCode: async function (focus) {
      if (!focus && this.customerCode != '' && this.selectedCustType['keyValue'] != '1') {
        this.selectedCustCode = this.populateObject(this.CustCodeData, 'Customer Code', this.customerCode.trim());
        if (!this.selectedCustCode) {
          this.$nextTick(() => {
            this.customerCodeSearch = this.customerCode;
          });
        }
      }
    },
    custTypeChange: async function (newSelector) {
      const temp = this.customerCode;
      if (newSelector) {
        if (newSelector['keyValue'] != '1') {
          this.billingCustCodeDisable = true;
          this.customerCodeSearch = temp;
        } else {
          this.billingCustCodeDisable = false;
          this.customerCodeSearch = '';
        }
      }
    },
    cancelCustSales: function () {
      this.showCustSalesDetails = false;
      this.clearSalesServiceTeamsField();
      this.selectedSalesService = [];
    },
    saveCustSales: function () {
      if (this.viewMode) return;
      if (this.checkTeamFields()) return;
      //If the user is editing, and the sale service that is currently being edited has the same system and job type from the old one, then skip these two blocks of check
      if (this.editSalesServiceIndex > -1) {
        if (
          !(
            this.selectedSystem['System'] === this.SalesServiceData[this.editSalesServiceIndex].System &&
            this.selectedJobType['JobType'] === this.SalesServiceData[this.editSalesServiceIndex].JobType
          )
        ) {
          for (const s of this.SalesServiceData) {
            if (this.selectedSystem['System'] === s.System && this.selectedJobType['JobType'] === s.JobType) {
              this.$showAlert(
                'error',
                'Cannot Save Sale Service',
                'This sale service already exists. Please ensure the System & JobType for each Sale Service is unique.',
              );
              return;
            }
          }
        }
      } else {
        for (const s of this.SalesServiceData) {
          if (this.selectedSystem['System'] === s.System && this.selectedJobType['JobType'] === s.JobType) {
            this.$showAlert(
              'error',
              'Cannot Save Sale Service',
              'This sale service already exists. Please ensure the System & JobType for each Sale Service is unique.',
            );
            return;
          }
        }
      }
      let temp = this.SalesServiceData;
      this.SalesServiceData = [];
      let team = {
        JobType: this.selectedJobType ? this.selectedJobType['JobType'] : '',
        SalesTeam_Code: this.selectedSalesTeam ? this.selectedSalesTeam['Sales Team Code'] : '',
        DefaultSalesPerson: this.SalesPerson,
        ServiceTeam_Code: this.selectedServiceTeam ? this.selectedServiceTeam['Service Team Code'] : '',
        DefaultServicePerson: this.ServicePerson,
        System: this.selectedSystem ? this.selectedSystem['System'] : '',
        DateUpdated: this.editSalesServiceIndex > -1 ? temp[this.editSalesServiceIndex]['DateUpdated'] : '',
        DefaultCust: this.Default ? 'Y' : '',
        UserID: this.editSalesServiceIndex > -1 ? temp[this.editSalesServiceIndex]['UserID'] : null,
        SalesAction: this.editSalesServiceIndex > -1 ? 'E' : 'N',
      };

      if (this.Default) {
        // Remove all previous default if the current one is default
        temp = temp.map((salesService) => {
          if (salesService.DefaultCust === 'Y') {
            // if this sales service team is default
            salesService.DefaultCust = ''; // remove default
            salesService.SalesAction = salesService.SalesAction === 'N' ? 'N' : 'E'; // and change action to send it to API, otherwise there'll be two defaults
          }
          return salesService; // do this for all default sales service teams, including the one being edited (if it's in edit mode)
        });
      }

      if (this.editSalesServiceIndex > -1) {
        temp.splice(this.editSalesServiceIndex, 1, team); // replace the row being edited that was changed by the default removal above
      } else {
        temp.push(team);
      }

      this.$nextTick(() => {
        this.SalesServiceData = temp;

        this.showCustSalesDetails = false;
        this.clearSalesServiceTeamsField();
        this.selectedSalesService = [];
      });
    },
    clearSalesServiceTeamsField: function () {
      this.selectedSystem = null;
      this.systemError = '';
      this.selectedJobType = null;
      this.jobTypeError = '';
      this.selectedSalesTeam = null;
      this.SalesPerson = '';
      this.salesPersonError = '';
      this.selectedServiceTeam = null;
      this.ServicePerson = '';
      this.servicePersonError = '';
      this.Default = false;

      this.editSalesServiceIndex = -1;
    },
    promptDiscardSalesAndServiceTeamChanges: function () {
      // $showAlert returns a promise, which can be awaited in the calling function to get the button clicked by the user
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved sales and service team changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    //#endregion

    //#region GST/SST/eInvoice methods
    addCustTaxExempt: function () {
      this.clearTaxExemptRecordField();
      this.showCustTaxExempt = true;
    },
    deleteCustTaxExempt: function () {
      if (this.selectedTaxRecord.length == 0) {
        this.$showAlert('error', 'No Record selected', 'Please select at least 1 record to delete');
        return;
      }

      let temp = this.TaxExemptData;
      this.TaxExemptData = [];

      temp = temp.filter((t) => t['ChargeCode'] !== this.selectedTaxRecord[0]['ChargeCode']);

      this.$nextTick(() => {
        this.TaxExemptData = temp;
        this.selectedTaxRecord = [];
      });
    },
    taxExemptTableBoxButtonClicked: function (btn, data) {
      if (this.viewMode) return;
      this.editTaxExemptIndex = this.TaxExemptData.findIndex((s) => s['TaxCode'] === data['TaxCode']);
      this.selectedChargeCode = this.ChargeCodeData.find(
        (d) =>
          d['Charge Code'].toLowerCase().trim() ==
          (data['ChargeCode'] ? data['ChargeCode'].toLowerCase().trim() : null),
      );
      this.selectedTETaxCode = this.TaxCodeData.find(
        (t) => t['Tax Code'].toLowerCase().trim() == (data['TaxCode'] ? data['TaxCode'].toLowerCase().trim() : null),
      );

      this.showCustTaxExempt = true;
    },
    checkTaxFields: function () {
      let error = false;
      if (!this.selectedChargeCode) {
        this.chargeCodeError = 'Charge Code is required!';
        error = true;
      }

      if (!this.selectedTETaxCode) {
        this.TETaxCodeError = 'Tax Code is required!';
        error = true;
      }

      return error;
    },
    cancelCustTaxExempt: function () {
      this.showCustTaxExempt = false;
      this.clearTaxExemptRecordField();
      this.selectedTaxRecord = [];
    },
    saveTaxExempt: function () {
      if (this.viewMode) return;
      if (this.checkTaxFields()) return;

      if (
        (this.editTaxExemptIndex > -1 &&
          !(this.selectedChargeCode['Charge Code'] === this.TaxExemptData[this.editTaxExemptIndex].ChargeCode)) ||
        this.editTaxExemptIndex < 0
      ) {
        const recordedCodes = this.TaxExemptData.map((t) => t.ChargeCode);
        if (recordedCodes.includes(this.selectedChargeCode['Charge Code'])) {
          this.$showAlert(
            'error',
            'Cannot Save Tax Exemption',
            'This Charge Code already has a tax code. Please ensure the Charge Code is unique.',
          );
          return;
        }
      }

      let temp = this.TaxExemptData;
      this.TaxExemptData = [];

      let record = {
        ChargeCode: this.selectedChargeCode ? this.selectedChargeCode['Charge Code'] : '',
        TaxCode: this.selectedTETaxCode ? this.selectedTETaxCode['Tax Code'] : '',
        UserID: this.editTaxExemptIndex > -1 ? temp[this.editTaxExemptIndex]['UserID'] : null,
        DateUpdated: this.editTaxExemptIndex > -1 ? temp[this.editTaxExemptIndex]['DateUpdated'] : null,
        // Since the record is considered as 'D' when the charge code is different/does not exists in the original Tax Exempt data, the edited row with updated charge code will be treated as 'N'
        taxAction:
          this.editTaxExemptIndex > -1 ?
            this.originalTaxExempt.map((t) => t.ChargeCode).includes(this.selectedChargeCode['Charge Code']) ?
              'E'
            : 'N'
          : 'N',
      };

      if (this.editTaxExemptIndex > -1) {
        temp.splice(this.editTaxExemptIndex, 1, record);
      } else {
        temp.push(record);
      }

      this.$nextTick(() => {
        this.TaxExemptData = temp;
        this.showCustTaxExempt = false;
        this.clearTaxExemptRecordField();
        this.selectedTaxRecord = [];
      });
      console.log(this.TaxExemptData);
    },
    clearTaxExemptRecordField: function () {
      this.selectedChargeCode = null;
      this.selectedTETaxCode = null;

      this.editTaxExemptIndex = -1;
    },
    promptDiscardTaxExemptChanges: function () {
      return this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved tax exemption changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },
    //#endregion

    validateTaxPayerTin: async function () {
      if (!this.eInvoiceTIN.startsWith('EI') && (!this.gstData['comRegNo'] || this.gstData['comRegNo'] == '')) {
        this.$showAlert('error', 'Missing ID Number', 'Please key in New Comp Reg No');
        return;
      }

      this.validateTINIcon = 'https://dsmlib.logistics.systems/Icons/LoadingCircle.gif';
      this.validateTINMsg = 'Validating';

      let jsonBody = {
        tin: this.eInvoiceTIN,
        brn: this.gstData['comRegNo'],
      };
      let result = await this.$apiCallV2('/ARInvoice/ValidateTaxpayerTin', jsonBody);
      if (result?.[0]?.['Results'] == 0) {
        this.validateTINIcon = 'https://dsmlib.logistics.systems/Icons/Save.png';
        this.validateTINMsg = 'Valid Taxpayer TIN';
      } else {
        this.validateTINIcon = 'https://dsmlib.logistics.systems/Icons/Cancel.png';
        this.validateTINMsg = `Invalid Taxpayer TIN (${result?.[0]?.['ErrMsg']})`;
      }
    },
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Customer') {
        x.data.forEach((k) => {
          if (k.Control === 'cmdExtCredit' && k.Properties === 'Visible') {
            this.SASShowExtCredit = k.PropertiesValue === 'True';
            return;
          }

          //key in menu same as control value from api
          let selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });

    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getCustomers().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/CustomerProfile/GetDefaults', { page: 'customer' }).then((result) => {
        this.DefaultGLAccData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    //#region API call General Details tab
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'customercategory' }).then((result) => {
        this.CustCategoryData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'customerstatus' }).then((result) => {
        this.StatusData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'branch' }).then((result) => {
        this.BrCodeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    //#endregion

    //#region API call Billing Info tab
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'paymenttype' }).then((result) => {
        this.DefaultPayTypeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billingtype' }).then((result) => {
        this.BillingTypeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billingformat' }).then((result) => {
        this.BillingFmtData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'currency' }).then((result) => {
        this.DefaultCurData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'glaccount' }).then((result) => {
        this.Cu_GLAccData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'internalcusttype' }).then((result) => {
        this.InternalCustData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'depotstoragebill' }).then((result) => {
        this.DepotStorageBillTypeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'invoiceoption' }).then((result) => {
        this.abInvoiceOptionData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    //#endregion

    //#region API call Credit Management tab
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'employee' }).then((result) => {
        this.CreditControllerData = result;
        this.ApprovedByData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'credittype' }).then((result) => {
        this.CreditTypeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    //#endregion

    //#region API call Sales & Service Team tab
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'systemsaleservice' }).then((result) => {
        this.SystemData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'jobtype' }).then((result) => {
        this.JobTypeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'salesteam' }).then((result) => {
        this.SalesTeamData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'serviceteam' }).then((result) => {
        this.ServiceTeamData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    //#endregion

    //#region API call GST/SST/eInvoice tab

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'artaxcode' }).then((result) => {
        let artaxcode = result.map((item) => ({
          ...item,
          'Tax Code': item['Tax Code'] ? item['Tax Code'].trim() : '',
          'Tax Code Desc': item['Tax Code Desc'] ? item['Tax Code Desc'].trim() : '',
          'Tax Type': item['Tax Type'] ? item['Tax Type'].trim() : '',
          'Tax Code - Tax Code Desc': `${item['Tax Code'] || ''} - ${item['Tax Code Desc'] || ''}`,
        }));
        this.TaxCodeData = artaxcode;
        this.gstData['taxCodeItems'] = artaxcode;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    // mountedPromises.push(
    //   this.$apiCallV2('/Master/GetListMaster', { masterfile: 'artaxcode' }).then((result) => {
    //     let artaxcode = result.map((item) => ({
    //       'Tax Code': item['Tax Code'] ? item['Tax Code'].trim() : '',
    //       'Tax Code Desc': item['Tax Code Desc'] ? item['Tax Code Desc'].trim() : '',
    //       'Tax Type': item['Tax Type'] ? item['Tax Type'].trim() : '', // Ensure Tax Type is included
    //       'Tax Code - Tax Code Desc': `${item['Tax Code'] || ''} - ${item['Tax Code Desc'] || ''}`,
    //     }));

    //     // Store the cleaned-up data properly
    //     this.TaxCodeData = artaxcode;
    //     this.gstData['taxCodeItems'] = [...artaxcode]; // Ensure reference consistency with a new array

    //     // Update loading status
    //     this.$topLoading(++promisesFulfilled / mountedPromises.length);
    //   })
    // );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODE' }).then((result) => {
        this.ChargeCodeData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    //#endregion

    //#region API call Address fields
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'postcode' }).then((result) => {
        this.postcodes = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'state' }).then((result) => {
        this.states = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' }).then((result) => {
        this.countries = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    // this.addressData['postcodeItems'] = this.postcodes;
    // this.addressData['stateItems'] = this.states;
    // this.addressData['countryItems'] = this.countries;
    //#endregion
    Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menus = this.menus.map((menus) => ({
        ...menus,
        enabled: true,
      }));
    });

    for (let i = 0; i < 31; i++) {
      this.daysData.push({ CodeValue: i + 1 < 10 ? `0${i + 1}` : `${i + 1}` });
      if (i < 3) {
        this.quarterData.push({ CodeValue: i + 1 < 10 ? `0${i + 1}` : `${i + 1}` });
      }
    }

    if (this.$route.query['new']) {
      this.menuClicked({ title: 'New Customer' });
    }
  },
  watch: {
    popupEntryShow: async function (val) {
      if (!val && this.$route.query['new'] && !this.viewMode) {
        // When the cancel is click but is not saved (for query new=1)
        let closeWindow = await this.$showAlert(
          'warning',
          'Cancelling Customer Creation',
          `You have not created an Customer profile.\nAre you sure you want to cancel?\n\nIf you cancel, this page will close.`,
          [{ text: 'Continue Creating' }, { text: 'Cancel Creating', color: 'red', textColor: 'white' }],
        );

        if (closeWindow == 'Cancel Creating') {
          // If user choose to cancel the creating process, it will close the tab
          window.close();
        } else {
          this.popupEntryShow = true;
        }
      } else if (!val && this.$route.query['new'] && this.viewMode) {
        window.close();
      }
    },

    eInvoiceTIN: function() {
      this.validateTINIcon = '';
    },

    'gstData.comRegNo': function() {
      this.validateTINIcon = '';
    },
    // currentTab: function(val) {
    //   if (val === 1) {
    //     this.$nextTick(() => {
    //       this.$refs["contactTab"].toggleErrorMessage(this.contactPersonError);
    //     });
    //   }
    // },
    // contactPersonError: function(val) {
    //   if (this.currentTab === 1) {
    //     this.$refs["contactTab"].toggleErrorMessage(this.contactPersonError);
    //   }
    // }
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.customer-main {
  padding: 10px 45px;
  font-size: 12px;
  display: flex;

  > .right-container {
    background-color: white;
    padding: 5px;
    border-radius: 5px;
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
  }
}

.customer-code {
  display: flex;

  > :last-child {
    margin-left: 5px;
  }
}

.rowBox {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  transition: 0.3s;

  > .rowTitle {
    white-space: nowrap;
    margin: 0 20px 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    > .rowReq {
      height: 100%;
      margin: 0 10px 0 0;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      color: #ff000088;
    }
  }

  > .shortInput {
    width: 30%;
    margin: 3px 10px 0 0;
  }
}

.scheduleDetails {
  width: 40%;
  margin-left: 10px;

  > .buttonBox {
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    padding: 10px 0 0 0;
  }

  > .scheduleBox {
    width: 100%;
    min-height: 20px;
    padding: 10px 5px 5px 5px;
    margin: 12px 0 0 0;
    position: relative;
    border: solid 1px gray;
    border-radius: 3px;

    > .scheduleTitle {
      padding: 0 5px 0 5px;
      position: absolute;
      top: -10px;
      left: 10px;
      background-color: white;
      color: gray;
    }

    > .weeklyBox {
      width: 100%;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    > .alertBox {
      width: 100%;
      height: auto;
      padding: 8px 10px 5px 10px;
      margin: 10px 0 0 0;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      color: #ff0000;
      border: solid 1px #ff000088;
      border-radius: 3px;

      > .alertTitle {
        padding: 0 5px 0 5px;
        font-weight: bold;
        position: absolute;
        top: -10px;
        left: 10px;
        background-color: white;
      }
    }
  }
}

.custCredTop {
  width: 100%;
  height: auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  > .custCredSection {
    flex: 1;

    > .custCredExtend {
      padding: 10px 5px 5px 5px;
      margin: 15px 5px 5px 5px;
      position: relative;
      border: solid 1px #00000033;
      border-radius: 3px;

      > .custCredExtendTitle {
        width: auto;
        height: 20px;
        padding: 0 5px 0 5px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        top: -11px;
        left: 10px;
        position: absolute;
        background-color: #ffffffff;
      }

      // > .custCredExtendButtons {
      //   margin: 0 0 0 20px;
      //   display: flex;
      //   flex-direction: column;
      // }
    }
  }
}

.custSalesDisplay {
  height: 100%;
}

.lhdn-tin {
  display: flex;
  flex-direction: row;
}

.custTaxDisplay {
  height: 100%;
}
</style>
